import type {
  AbuseControlsOverride,
  BigWinnerOverride,
  CategoryBonusOverride,
  ConfigSurface,
  DragontigerMultipliersOverride,
  DragontigerWeightsOverride,
  GreedykingCategoryBonusOverride,
  GreedykingJackpotOverride,
  GreedykingMultipliersOverride,
  GreedykingWeightsOverride,
  JackpotOverride,
  LeaderboardOverride,
  MultipliersOverride,
  RulesOverride,
  SchedulerOverride,
  SicboJackpotOverride,
  SicboMultipliersOverride,
  SicboWeightsOverride,
  StreakOverride,
  TableOverride,
  IntegratorGameConfig,
  TimingOverride,
  WeightsOverride,
} from '@apx/integrator-config/schemas';

export type {
  AbuseControlsOverride,
  BigWinnerOverride,
  CategoryBonusOverride,
  ConfigSurface,
  DragontigerMultipliersOverride,
  DragontigerWeightsOverride,
  GreedykingCategoryBonusOverride,
  GreedykingJackpotOverride,
  GreedykingMultipliersOverride,
  GreedykingWeightsOverride,
  JackpotOverride,
  LeaderboardOverride,
  MultipliersOverride,
  RulesOverride,
  SchedulerOverride,
  SicboJackpotOverride,
  SicboMultipliersOverride,
  SicboWeightsOverride,
  StreakOverride,
  TableOverride,
  IntegratorGameConfig,
  TimingOverride,
  WeightsOverride,
};

export interface MeResponse {
  userId: string;
  email: string | null;
  roles: Array<{ role: string; integratorId?: string }>;
  permissions: string[];
  isSuperAdmin: boolean;
  /** True when every grant is tenant-scoped — integrator staff, not provider staff. */
  isLocked?: boolean;
  /** Integrators a locked principal may act on. Empty for provider staff. */
  tenantLock?: string[];
}

export interface TenantStaffMember {
  id: string;
  email: string;
  status: string;
  lastLoginAt: string | null;
  permissions: string[];
}

export interface TenantStaffCatalogue {
  /** Capabilities the calling admin may hand on — already intersected server-side. */
  permissions: string[];
  presets: Array<{ name: string; permissions: string[] }>;
}

export interface AdminClientOptions {
  baseUrl: string;
  /**
   * Bearer token (issue-admin-token / dev paste-token flow). OPTIONAL — in the
   * normal browser flow auth rides the HttpOnly session cookie set at login
   * (sent via `credentials: 'include'`), so no token is held in JS (C12).
   */
  token?: string;
  /** Optional fetch override (defaults to global fetch). */
  fetch?: typeof fetch;
  /**
   * Invoked once for any 401 the client receives, just before the
   * `AdminApiError` is thrown. Lets the host app centralise session-expiry
   * handling (clear token, route to the auth screen) instead of every page
   * rendering a raw `UNAUTHORIZED` string. The error still throws so the
   * caller's own catch runs too.
   */
  onUnauthorized?: (err: AdminApiError) => void;
}

export interface IntegratorSummary {
  id: string;
  slug: string;
  name: string;
  status: string;
  defaultLocale: string;
  defaultFiatCurrency: string;
  createdAt: string;
  isProvider: boolean;
}

/** Per-integrator last-24h KPI roll-up returned by /v1/integrators/summary.
 *  Drives the Provider Overview page + KPI columns on the tenants list. */
export interface IntegratorKpiRow {
  id: string;
  slug: string;
  name: string;
  status: string;
  defaultFiatCurrency: string;
  createdAt: string;
  kpi24h: {
    turnover: string;
    payout: string;
    ggr: string;
    rtp: number;
    betCount: number;
    rounds: number;
    players: number;
  };
  /** Drift + reachability surface for the Provider overview. */
  reconcile?: {
    unresolvedDrift: number;
    failedCalls: number;
    lastCallAt: string | null;
    lastCallStatus: string | null;
    lastCallLatencyMs: number | null;
  };
}

/** Per-integrator security + wallet configuration exposed by the detail route.
 *  Never includes the plaintext walletAuthSecret — only a configured flag. */
export interface TenantSecurity {
  allowedIps: string[];
  allowedOrigins: string[];
  walletBaseUrl: string | null;
  walletTimeoutMs: number;
  walletDriftTolerance: string;
  walletAuthSecretConfigured: boolean;
  /** Previous secret still accepted during the overlap window. */
  walletAuthSecretPrevConfigured: boolean;
  /** ISO timestamp of last rotation, null if never rotated. */
  walletAuthSecretRotatedAt: string | null;
}

export interface IntegratorDetail {
  integrator: IntegratorSummary & TenantSecurity;
  counts: {
    players: number;
    apiKeys: number;
    webhooks: number;
    pendingApprovals: number;
    /** Unresolved WalletDrift rows for this integrator. */
    unresolvedDrift?: number;
    /** WalletTransaction rows in failed/reconcile_manual state. */
    failedWalletCalls?: number;
  };
  kpi24h: {
    rounds: number;
    totalBet: string;
    totalPayout: string;
  };
  recentRounds: Array<{
    id: string;
    gameId: string;
    state: string;
    startedAt: string;
    endedAt: string | null;
    totalBet: string;
    totalPayout: string;
    participantCount: number;
  }>;
  recentAudits: Array<{
    id: string;
    action: string;
    targetType: string;
    targetId: string;
    actorUserId: string;
    createdAt: string;
  }>;
}

export interface AdminUserRoleGrant {
  grantId: string;
  role: string;
  integratorId: string | null;
  tenantSlug: string | null;
}

/**
 * Capabilities a user holds on one integrator, collapsed to a count. Tenant
 * staff are granted these instead of a role binding, so a user with an empty
 * `roles` array can still be fully active.
 */
export interface AdminUserCapabilityGrant {
  integratorId: string;
  tenantSlug: string;
  tenantName: string;
  count: number;
}

export interface AdminUserView {
  id: string;
  email: string;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
  roles: AdminUserRoleGrant[];
  capabilityGrants: AdminUserCapabilityGrant[];
}

/** Platform-level supported-currency registry row (native-currency model —
 *  not a fiat conversion rate; the currency itself is the unit of account). */
export interface SupportedCurrencyView {
  code: string;
  displayName: string;
  symbol: string | null;
  /** Fixed by the platform per code (USD 2 — counted in cents; every other
   *  currency 0 — whole units). Read-only: see `createCurrency`. */
  decimals: number;
  enabled: boolean;
  sortOrder: number;
}

/**
 * Rolling availability % from the background sampler, per window. pcts are
 * null until a window has at least one sample; `coverageMs` is how long we've
 * actually been sampling (so a 100% over 10 minutes can be caveated).
 */
export interface Availability {
  pct1h: number | null;
  pct24h: number | null;
  pct7d: number | null;
  coverageMs: number;
}

export interface InfraProbe {
  status: 'up' | 'down';
  latencyMs: number;
  error?: string;
  /** Rolling uptime % over windows. Absent on older backends. */
  availability?: Availability;
}

export interface ServiceProbeResult {
  name: string;
  status: 'up' | 'down';
  latencyMs: number;
  /** The /health URL probed (absent for the synthetic admin-api row). */
  url?: string;
  /** True for admin-api — it is the aggregator, not actually probed. */
  self?: boolean;
  /** Process uptime reported by /health — how long it has run since (re)start.
   *  Absent if the service predates the field or is down. */
  uptimeMs?: number;
  /** Rolling uptime % over windows. Absent on older backends. */
  availability?: Availability;
  error?: string;
  payload?: unknown;
}

export interface IntegratorProbe {
  integratorId: string;
  slug: string;
  name: string;
  walletBaseUrl: string;
  status: 'up' | 'down' | 'skipped';
  latencyMs: number;
  httpStatus?: number;
  error?: string;
}

export interface SystemHealth {
  /** gamehub-owned health only (infra + our services). */
  overall: 'healthy' | 'degraded';
  /**
   * Reachability of integrators' third-party wallet endpoints — reported
   * separately so an external endpoint being down does not mark the apx
   * system as degraded. Absent on older backends.
   */
  integratorsStatus?: 'healthy' | 'degraded';
  checkedAt: string;
  infra: {
    postgres: InfraProbe;
    redis: InfraProbe;
  };
  services: ServiceProbeResult[];
  integrators?: IntegratorProbe[];
}

// ─────────── Redis monitoring (superadmin-only; 403 for everyone else)

/** AOF/RDB posture. Straight from INFO persistence. */
export interface RedisPersistence {
  aofEnabled: boolean;
  /** Always true when aofEnabled is false — Redis omits the underlying field
   *  then, and reporting false would be a permanent false-positive page. */
  aofLastWriteOk: boolean;
  aofRewriteInProgress: boolean;
  rdbLastBgsaveOk: boolean;
  rdbBgsaveInProgress: boolean;
  changesSinceLastSave: number;
}

/**
 * Server-computed capacity band. The thresholds (normal <70%, warning 70-84%,
 * critical >=85%) are env-configurable backend-side, so the UI consumes this
 * string and NEVER re-derives them — two copies would silently drift.
 *
 * 'unknown' means no memory limit is configured, so no band exists. It is not
 * a synonym for 'normal': an uncapped Redis is the #446 root cause.
 */
export type RedisCapacityStatus = 'normal' | 'warning' | 'critical' | 'unknown';

export interface RedisMemory {
  usedBytes: number;
  /** What the kernel — and therefore the cgroup OOM killer — sees. */
  rssBytes: number;
  datasetBytes: number;
  peakBytes: number;
  /** null when `maxmemory` is 0, i.e. UNCONFIGURED. Not "no usage". */
  maxBytes: number | null;
  /** 0-100, one decimal. null when there is no limit to be a fraction of. */
  usedPct: number | null;
  freeBytes: number | null;
  policy: string;
  /** null when Redis reported `inf` (fresh instance). */
  fragmentationRatio: number | null;
}

export interface RedisKeyspace {
  keyCount: number;
  evictedKeys: number;
  expiredKeys: number;
}

export interface RedisClients {
  connected: number;
  blocked: number;
}

export interface RedisServerInfo {
  version: string;
  uptimeSeconds: number;
}

export type RedisWarningCode =
  | 'NO_MAXMEMORY'
  | 'EVICTION_POLICY_LOSSY'
  | 'EVICTIONS_OBSERVED'
  | 'BGSAVE_FAILING'
  | 'AOF_WRITE_FAILING'
  | 'HIGH_FRAGMENTATION'
  | 'NO_CONTAINER_LIMIT_CONFIGURED';

export interface RedisWarning {
  code: RedisWarningCode;
  severity: 'warning' | 'critical';
  message: string;
}

/** One namespace prefix's share of the keyspace, from the bounded SCAN. */
export interface RedisNamespace {
  prefix: string;
  count: number;
}

export interface RedisCapacity {
  connection: 'up' | 'down';
  /** Populated only when connection is 'down'. A Redis outage is returned as
   *  HTTP 200 with this set — it is data this page exists to show. */
  error?: string;
  checkedAt: string;
  /** All five present iff connection === 'up'. */
  memory?: RedisMemory;
  keyspace?: RedisKeyspace;
  clients?: RedisClients;
  persistence?: RedisPersistence;
  server?: RedisServerInfo;
  status: RedisCapacityStatus;
  thresholds: { warnPct: number; criticalPct: number };
  /** Operator-supplied cgroup limit; Redis cannot report its own. null when
   *  REDIS_CONTAINER_MEMORY_BYTES is unset. */
  containerLimitBytes: number | null;
  containerUsedPct: number | null;
  /** null until the sampler's first namespace scan lands. */
  namespaces: RedisNamespace[] | null;
  /** True when the scan hit its cursor budget before exhausting the keyspace. */
  namespacesPartial: boolean;
  namespacesAt: string | null;
  warnings: RedisWarning[];
}

/**
 * 1h/24h/7d come from the raw 60s snapshots (kept 8 days); 30d/90d/1y come
 * from the daily rollup (kept 400 days). The split is why a year of history
 * costs 365 rows instead of 525,000 — and why the short windows still exist at
 * full resolution for incident work.
 */
export type RedisHistoryWindow = '1h' | '24h' | '7d' | '30d' | '90d' | '1y';

/** True for the windows served by the daily rollup, i.e. the ones whose points
 *  carry the daily-only fields on RedisHistoryPoint. */
export function isDailyRedisWindow(w: RedisHistoryWindow): boolean {
  return w === '30d' || w === '90d' || w === '1y';
}

export interface RedisHistoryPoint {
  /** Bucket start, ISO. */
  t: string;
  /** Samples in this bucket. Empty buckets are OMITTED rather than
   *  zero-filled, so a monitoring gap reads as a gap. */
  samples: number;
  usedAvgBytes: number;
  usedMaxBytes: number;
  /** A rising FLOOR is the leak signal: a peak can be one fat client buffer,
   *  but a baseline that never comes back down is dataset growth. */
  usedMinBytes: number;
  rssMaxBytes: number;
  datasetAvgBytes: number;
  datasetMaxBytes: number;
  maxMemoryBytes: number | null;
  keyCountMax: number;
  connectedClientsMax: number;
  fragmentationMax: number | null;
  /** Raw windows only — the monotonic counter's max in the bucket. null on
   *  daily windows, which carry the restart-aware delta instead. */
  evictedKeysMax: number | null;
  expiredKeysMax: number | null;
  /** A drop versus the previous bucket marks a Redis restart. */
  uptimeMinSeconds: number;

  // ── Daily windows only (see isDailyRedisWindow); null on raw windows.
  //    Collapsing a day to one point destroys what an operator actually looks
  //    for: a daily max says "it was bad", `usedMaxAt` says when,
  //    `samplesCritical` says for how long, and `probeFailures` says whether
  //    the monitor could see anything at all.

  /** Instant of the day's peak, ISO. */
  usedMaxAt: string | null;
  /** Failed probes that day. Non-zero next to a low `samples` is an outage. */
  probeFailures: number | null;
  /** Worst capacity status the day reached. */
  worstStatus: RedisCapacityStatus | null;
  /** Disjoint counts — samples at/above warn but below critical, and samples
   *  at/above critical — so they read as "42 warn / 8 critical". Duration is
   *  what separates "touched critical once" from "sat there for 40 minutes". */
  samplesWarn: number | null;
  samplesCritical: number | null;
  /** Redis restarts detected within the day. */
  restartCount: number | null;
  /** Restart-aware increments over the day. Under `noeviction`,
   *  evictedKeysDelta must stay 0. */
  evictedKeysDelta: number | null;
  expiredKeysDelta: number | null;
  /** maxmemory was not constant across the day — somebody set or unset the
   *  cap. Unsetting it is the #446 root cause. */
  maxMemoryChanged: boolean | null;
}

/** A day Redis was unreachable for its entirety: a rollup row exists, but no
 *  successful sample landed in it. */
export interface RedisOutageDay {
  /** Midnight UTC, ISO. */
  t: string;
  probeFailures: number;
}

export interface RedisHistory {
  window: RedisHistoryWindow;
  bucketSeconds: number;
  from: string;
  to: string;
  /** Ascending by t, bounded at roughly 30-365 points: raw windows are
   *  server-downsampled with date_bin, daily windows are one stored rollup row
   *  per day. Never the raw per-minute rows. */
  points: RedisHistoryPoint[];
  /** Daily windows only: whole days with no successful sample. Reported apart
   *  from `points` rather than plotted as zeroes, so the chart never draws a
   *  memory cliff that never happened. Empty on raw windows, where an omitted
   *  bucket already reads as a gap. */
  outages: RedisOutageDay[];
  status: RedisCapacityStatus;
  thresholds: { warnPct: number; criticalPct: number };
}

export type WalletTestPingOutcome =
  | {
      ok: true;
      status: number;
      latencyMs: number;
      bodySnippet: string;
    }
  | {
      ok: false;
      status: number | null;
      latencyMs: number;
      error: string;
      bodySnippet?: string;
    };

export interface WalletLedgerEntry {
  id: string;
  playerId: string;
  playerUsername: string | null;
  playerIsAnonymous: boolean;
  /** Integrator-call log types. Legacy DEBIT/CREDIT/ADJUST may
   *  still appear on rows written before the pivot. */
  type:
    | 'DEBIT_BET'
    | 'CREDIT_WIN'
    | 'CREDIT_REFUND'
    | 'ROLLBACK'
    | 'RECONCILE_MANUAL'
    | 'DEBIT'
    | 'CREDIT'
    | 'ADJUST'
    | string;
  status?: string;
  /** The player's currency — the unit of `creditAmount` and `balanceAfter`,
   *  which are integer minor units of it (USD in cents). */
  currency: string;
  creditAmount: string;
  fiatAmount: string | null;
  fiatCurrency: string;
  ratePpm: string | null;
  parentTxId: string | null;
  balanceAfter: string | null;
  integratorTxId?: string | null;
  attempt?: number | null;
  latencyMs?: number | null;
  lastError?: string | null;
  reason: string;
  requestId: string;
  refTxId: string | null;
  createdAt: string;
  /** Failed-call triage — set when an admin has acknowledged this failed row.
   *  Present only on failed-call listings; the trace view may omit them. */
  acknowledgedAt?: string | null;
  acknowledgedBy?: string | null;
  acknowledgeNote?: string | null;
}

export interface PlayerView {
  id: string;
  integratorId: string;
  username: string | null;
  isAnonymous: boolean;
  banned: boolean;
  integratorPlayerRef: string | null;
  /** Fixed at registration; a player only ever holds this one currency. */
  currency: string;
  createdAt: string;
  settings: { soundEnabled: boolean; musicEnabled: boolean; locale: string } | null;
  /** gamehub-side credit balance (WalletShadow.creditBalance). String to
   *  preserve BigInt precision. "0" if no shadow row yet. */
  creditBalance: string;
}

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'reverted';

export interface ApprovalView {
  id: string;
  integratorId: string;
  integratorName: string | null;
  integratorSlug: string | null;
  gameId: string;
  surface: ConfigSurface;
  /** `reverted` only occurs on rows belonging to a bulk batch. */
  status: ApprovalStatus;
  proposerUserId: string;
  approverUserId: string | null;
  /** On a bulk row, null means the integrator had no override for the surface. */
  prevConfig: unknown;
  pendingConfig: unknown;
  reason: string | null;
  /** Set when the row is one integrator × game of a bulk change. Such rows are
   *  decided through the bulk endpoints, never individually. */
  batchId: string | null;
  createdAt: string;
  decidedAt: string | null;
}

/** Filters for the provider-wide approvals list and export. */
export interface ApprovalFilters {
  status?: ApprovalStatus | 'all';
  integratorIds?: string[];
  gameId?: string;
  surface?: ConfigSurface;
  batchId?: string;
  /** ISO datetime, inclusive. */
  from?: string;
  to?: string;
}

export interface ApprovalsPage {
  approvals: ApprovalView[];
  nextCursor: string | null;
  /** Rows matching the filters across ALL pages. */
  total: number;
  /** Per-integrator counts over the whole filtered set, largest first. */
  byIntegrator: Array<{
    integratorId: string;
    integratorName: string | null;
    integratorSlug: string | null;
    count: number;
  }>;
}

/** Provider-owned surfaces a bulk change may write. */
export const BULK_SURFACES = [
  'weights',
  'multipliers',
  'timing',
  'scheduler',
  'abuse',
  'envelope',
  'jackpot',
  'maintenance',
] as const satisfies readonly ConfigSurface[];
export type BulkSurface = (typeof BULK_SURFACES)[number];

export type BulkBatchStatus = 'pending' | 'approved' | 'rejected' | 'reverted';

/**
 * set     — write a value as each integrator's override.
 * inherit — remove each integrator's override so it follows the provider
 *           default, including later provider edits.
 */
export type BulkMode = 'set' | 'inherit';

export interface BulkBatchView {
  id: string;
  gameIds: string[];
  surface: BulkSurface;
  mode: BulkMode;
  pendingConfig: unknown;
  status: BulkBatchStatus;
  note: string | null;
  proposerUserId: string;
  approverUserId: string | null;
  /** Rejection reason. */
  reason: string | null;
  createdAt: string;
  decidedAt: string | null;
  revertedByUserId: string | null;
  revertReason: string | null;
  revertedAt: string | null;
  itemCount: number | null;
}

export interface BulkPreviewItem {
  integratorId: string;
  integratorName: string;
  integratorSlug: string;
  integratorStatus: string;
  gameId: string;
  /** Null when the integrator has no override for this surface today. */
  prevConfig: unknown;
  /** Null on an inherit: the override is removed. */
  nextConfig: unknown;
  /** Inherit only: the provider default it will follow (null ⇒ game defaults). */
  inheritedConfig?: unknown;
  changed: boolean;
  /** Non-empty blocks the proposal (non-super admins only). */
  rtpCapViolations: string[];
  /** Pending single-integrator approvals on this row; approving the batch rejects them as superseded. */
  conflictingApprovalIds: string[];
  /** Envelope batches: stored values already outside the new envelope. Warning only. */
  envelopeViolations: BulkEnvelopeViolation[];
}

/** A value an integrator already stores that falls outside the envelope it would get. */
export interface BulkEnvelopeViolation {
  surface: string;
  field: string;
  value: number;
  bound: 'min' | 'max';
  limit: number;
}

export interface BulkBatchDetail {
  batch: BulkBatchView;
  /** Warnings are filled for pending rows of a pending batch, from current values. */
  items: Array<ApprovalView & { envelopeViolations: BulkEnvelopeViolation[]; conflictingApprovalIds: string[] }>;
  outOfEnvelope: number;
  /** Pending single approvals that approving this batch would close. */
  supersedes: number;
}

export interface BulkPreviewSummary {
  targets: number;
  changed: number;
  unchanged: number;
  blocked: number;
  /** Rows left with stored values outside their new envelope. */
  outOfEnvelope: number;
  /** Pending single approvals that approving this batch would close. */
  supersedes: number;
  integrators: number;
}

export interface BulkProposeInput {
  surface: BulkSurface;
  gameIds: string[];
  /** Omit for every integrator that has the game. */
  integratorIds?: string[];
  /** Default 'set'. */
  mode?: BulkMode;
  /** Required for 'set'; must be omitted for 'inherit'. */
  value?: unknown;
  note?: string;
}

export interface BulkPreview {
  summary: BulkPreviewSummary;
  preview: BulkPreviewItem[];
  /** Named integrator × game pairs with no provisioned game. */
  skipped: Array<{ integratorId: string; gameId: string; reason: string }>;
}

export type BulkItemOutcome = 'applied' | 'reverted' | 'missing' | 'drifted';

export interface BulkDecisionResult {
  status: BulkBatchStatus;
  batch: BulkBatchView;
  outcomes?: Record<BulkItemOutcome, number>;
  results?: Array<{ approvalId: string; integratorId: string; gameId: string; outcome: BulkItemOutcome }>;
  /** Approve only: pending single approvals rejected as superseded by this batch. */
  supersededApprovalIds?: string[];
  auditFailures?: number;
}

// ─────────── Transaction trace (admin chain trace)
//
// DTO mirror of the admin-api `ChainTrace` JSON. Declared locally so the SDK
// stays decoupled from the backend package. All money/amount fields are
// strings to preserve BigInt precision over JSON.

export type ChainMovementKind = 'debit' | 'win' | 'jackpot' | 'rollback' | 'other';

export interface ChainMovement {
  kind: ChainMovementKind;
  walletTxId: string;
  requestId: string;
  refTxId: string | null;
  parentTxId: string | null;
  creditAmount: string;
  status: string;
  sealed: boolean;
  createdAt: string;
}

export interface ChainBet {
  betId: string;
  requestId: string;
  playerId: string;
  betType: string;
  /** The bet's currency (`Bet.currency`). `stake`, `payout` and every
   *  movement's `creditAmount` are integer minor units of it (USD in cents).
   *  Optional: an older API build omits it. */
  currency?: string | null;
  stake: string;
  outcome: string;
  /** Debit-acceptance lifecycle (confirmed | pending | rejected | cancelled),
   *  orthogonal to `outcome`: only the pair identifies a row — PENDING+confirmed
   *  is a live bet, PENDING+rejected a debit the wallet declined. Rendered
   *  beside `outcome` on every surface that shows one. */
  acceptStatus: string;
  payout: { baseCredits: string; matchCredits: string; jackpotCredits: string } | null;
  movements: ChainMovement[];
}

export interface ChainTraceRound {
  roundId: string;
  gameId: string;
  state: string;
  startedAt: string;
  endedAt: string | null;
  resultData: unknown;
  verifyVerdict?: 'VALID' | 'INVALID' | 'UNVERIFIABLE';
}

export interface ChainTrace {
  round: ChainTraceRound;
  bets: ChainBet[];
}

export interface PlayerHistoryResult {
  rounds: ChainTrace[];
  nextCursor: string | null;
}

/** True totals for a player's history over the whole filter set (not a page).
 *  Amounts are decimal strings of integer credit units. */
export interface PlayerHistorySummary {
  totalRounds: number;
  /** The player's currency — the unit of the three totals, which are integer
   *  minor units of it. Optional: an older API build omits it. */
  currency?: string | null;
  wagered: string;
  won: string;
  net: string;
}

export class AdminApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

/** Maintenance flag shape returned by the maintenance endpoints. */
export interface MaintenanceView {
  enabled: boolean;
  message: string;
  since?: string;
}

export class AdminClient {
  private readonly baseUrl: string;
  private readonly token?: string;
  private readonly fetchImpl: typeof fetch;
  private readonly onUnauthorized?: (err: AdminApiError) => void;

  constructor(opts: AdminClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.token = opts.token;
    // Bind to globalThis so browsers don't throw "Illegal invocation" when
    // calling a detached fetch reference.
    this.fetchImpl = opts.fetch ?? fetch.bind(globalThis);
    this.onUnauthorized = opts.onUnauthorized;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {
      // Custom header the cookie-CSRF guard checks; a cross-site form/img can't
      // set it. Harmless on GET. Server still accepts legacy `x-gamehub-csrf`.
      'x-apx-csrf': '1',
    };
    if (this.token) headers['authorization'] = `Bearer ${this.token}`;
    if (body !== undefined) headers['content-type'] = 'application/json';
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      // Send the HttpOnly session cookie on same-site XHR (admin → api).
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok) {
      let payload: { error?: string; message?: string; details?: unknown } = {};
      try {
        payload = (await res.json()) as typeof payload;
      } catch {
        // fall through
      }
      const err = new AdminApiError(
        res.status,
        payload.error ?? `HTTP_${res.status}`,
        payload.message ?? res.statusText,
        payload.details,
      );
      // Centralised session-expiry hook — fire before throwing so the host app
      // can route to the auth screen regardless of which page made the call.
      if (res.status === 401) this.onUnauthorized?.(err);
      throw err;
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  // ─────────── Tenants
  /** Current session identity + effective permissions (server-derived). The
   *  SPA uses this instead of decoding the JWT (now an HttpOnly cookie). */
  async me(): Promise<MeResponse> {
    return this.request<MeResponse>('GET', '/v1/me');
  }

  /** Clear the server-side session cookie. */
  async logout(): Promise<void> {
    await this.request<{ ok: boolean }>('POST', '/v1/auth/logout');
  }

  async listIntegrators(): Promise<IntegratorSummary[]> {
    const { tenants } = await this.request<{ tenants: IntegratorSummary[] }>(
      'GET',
      '/v1/integrators',
    );
    return tenants;
  }

  /** Cross-integrator KPI roll-up — used by Provider Overview + tenants list. */
  async listIntegratorsSummary(): Promise<IntegratorKpiRow[]> {
    const { tenants } = await this.request<{ tenants: IntegratorKpiRow[] }>(
      'GET',
      '/v1/integrators/summary',
    );
    return tenants;
  }

  async fetchIntegratorDetail(integratorId: string): Promise<IntegratorDetail> {
    return this.request('GET', `/v1/integrators/${integratorId}/detail`);
  }

  // ─────────── System health
  async fetchSystemHealth(): Promise<SystemHealth> {
    return this.request('GET', '/v1/health/system');
  }

  // ─────────── Redis monitoring (superadmin-only; 403 for everyone else)
  /** Live capacity + health. Returns 200 with `connection: 'down'` when Redis
   *  is unreachable — that is the answer, not an error. */
  async fetchRedisCapacity(): Promise<RedisCapacity> {
    return this.request('GET', '/v1/system/redis/usage');
  }

  /** Bucketed usage history for one window. Observed state only — there is
   *  deliberately no projection; see redis-usage-history.ts. */
  async fetchRedisHistory(window: RedisHistoryWindow = '24h'): Promise<RedisHistory> {
    const params = new URLSearchParams({ window });
    return this.request('GET', `/v1/system/redis/history?${params.toString()}`);
  }

  // ─────────── Round operations
  async forceFinishRound(roundId: string, reason: string): Promise<{ ok: true; roundId: string }> {
    return this.request('POST', `/v1/rounds/${roundId}/force-finish`, { reason });
  }

  async refundBet(
    betId: string,
    reason: string,
  ): Promise<{
    betId: string;
    playerId: string;
    refundAmount: string;
    integratorStatus: 'confirmed' | 'rolled_back' | 'noop';
    integratorTxId: string | null;
    newBalance: string;
    currency: string;
  }> {
    return this.request('POST', `/v1/bets/${betId}/refund`, { reason });
  }

  async startManualRound(input: {
    gameId: string;
    integratorId: string;
    scopeKey?: string;
    clientSeed?: string;
  }): Promise<{ id: string; gameId: string; state: string }> {
    return this.request('POST', '/v1/rounds', input);
  }

  // ─────────── Admin users
  async listAdminUsers(): Promise<AdminUserView[]> {
    const { users } = await this.request<{ users: AdminUserView[] }>('GET', '/v1/admin-users');
    return users;
  }

  async createAdminUser(input: {
    email: string;
    password: string;
  }): Promise<{ id: string; email: string; status: string }> {
    return this.request('POST', '/v1/admin-users', input);
  }

  async updateAdminUser(
    userId: string,
    patch: { status?: 'active' | 'disabled'; password?: string },
  ): Promise<{ id: string; email: string; status: string }> {
    return this.request('PATCH', `/v1/admin-users/${userId}`, patch);
  }

  async grantAdminRole(
    userId: string,
    input: { role: string; integratorId?: string },
  ): Promise<{ grantId: string; role: string; integratorId: string | null }> {
    return this.request('POST', `/v1/admin-users/${userId}/roles`, input);
  }

  async revokeAdminRole(userId: string, grantId: string): Promise<void> {
    await this.request('DELETE', `/v1/admin-users/${userId}/roles/${grantId}`);
  }

  // ─────────── Currencies (platform-level supported-currency registry)
  async listCurrencies(): Promise<SupportedCurrencyView[]> {
    const { currencies } = await this.request<{ currencies: SupportedCurrencyView[] }>(
      'GET',
      '/v1/admin/currencies',
    );
    return currencies;
  }

  async createCurrency(input: {
    code: string;
    displayName: string;
    symbol?: string;
    /** Leave unset. A currency's decimals are fixed by the platform; the API
     *  rejects a value that disagrees (`DECIMALS_FIXED`). */
    decimals?: number;
    sortOrder?: number;
    enabled?: boolean;
  }): Promise<SupportedCurrencyView> {
    const { currency } = await this.request<{ currency: SupportedCurrencyView }>(
      'POST',
      '/v1/admin/currencies',
      input,
    );
    return currency;
  }

  async updateCurrency(
    code: string,
    patch: {
      displayName?: string;
      symbol?: string | null;
      /** Leave unset — fixed by the platform, see `createCurrency`. */
      decimals?: number;
      enabled?: boolean;
      sortOrder?: number;
    },
  ): Promise<SupportedCurrencyView> {
    const { currency } = await this.request<{ currency: SupportedCurrencyView }>(
      'PATCH',
      `/v1/admin/currencies/${encodeURIComponent(code)}`,
      patch,
    );
    return currency;
  }

  async listWalletTransactions(
    integratorId: string,
    params: {
      playerId?: string;
      type?: string;
      status?: string;
      fiatCurrency?: string;
      from?: string;
      to?: string;
      /** Triage filter for the failed-calls view: 'false' = un-acknowledged
       *  only, 'true' = acknowledged only, absent = all. */
      acknowledged?: 'true' | 'false' | 'all';
      limit?: number;
      cursor?: string;
    } = {},
  ): Promise<{ entries: WalletLedgerEntry[]; nextCursor?: string }> {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') query.set(k, String(v));
    }
    const qs = query.toString();
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/wallet-transactions${qs ? `?${qs}` : ''}`,
    );
  }

  // ─────────── Game config
  async getEffectiveConfig(
    integratorId: string,
    gameId: string,
  ): Promise<{ integratorId: string; gameId: string; config: IntegratorGameConfig }> {
    return this.request('GET', `/v1/integrators/${integratorId}/games/${gameId}/config`);
  }

  /** Layered config for the inherited-vs-override UI: the effective config,
   *  the provider-default config, this integrator's RAW own override, and
   *  whether the queried tid IS the provider scope. Backed by the
   *  `…/config/effective` endpoint. Distinct from `getEffectiveConfig`, which
   *  returns only this integrator's own merged config (no provider layering). */
  async getConfigInheritance(
    integratorId: string,
    gameId: string,
  ): Promise<{
    effective: IntegratorGameConfig;
    providerDefault: IntegratorGameConfig;
    ownOverride: IntegratorGameConfig;
    isProviderScope: boolean;
  }> {
    return this.request('GET', `/v1/integrators/${integratorId}/games/${gameId}/config/effective`);
  }

  /** Emergency kill switch fast-path. Bypasses two-admin approval
   *  so an on-call admin can stop bets immediately. */
  async setKillSwitch(
    integratorId: string,
    gameId: string,
    engaged: boolean,
  ): Promise<{
    engaged: boolean;
    abuse: { killSwitch?: boolean; betCapPerRound?: number; betRateLimitPerSecond?: number };
  }> {
    return this.request(
      'POST',
      `/v1/integrators/${integratorId}/games/${gameId}/abuse/kill-switch`,
      { engaged },
    );
  }

  /** Read the GLOBAL maintenance flag (platform-wide, all games). */
  async getGlobalMaintenance(): Promise<{ maintenance: MaintenanceView }> {
    return this.request('GET', '/v1/maintenance');
  }

  /** Toggle the GLOBAL maintenance flag — stops every game for every
   *  integrator at once. Requires a global (super_admin) grant. */
  async setGlobalMaintenance(
    enabled: boolean,
    message?: string,
  ): Promise<{ maintenance: MaintenanceView }> {
    return this.request('POST', '/v1/maintenance', { enabled, message });
  }

  /** Toggle maintenance for a single integrator+game. */
  async setGameMaintenance(
    integratorId: string,
    gameId: string,
    enabled: boolean,
    message?: string,
  ): Promise<{ maintenance: MaintenanceView }> {
    return this.request('POST', `/v1/integrators/${integratorId}/games/${gameId}/maintenance`, {
      enabled,
      message,
    });
  }

  async getSurface<S extends ConfigSurface>(
    integratorId: string,
    gameId: string,
    surface: S,
  ): Promise<{ surface: S; value: IntegratorGameConfig[S] | null }> {
    return this.request('GET', `/v1/integrators/${integratorId}/games/${gameId}/config/${surface}`);
  }

  async proposeSurface<S extends ConfigSurface>(
    integratorId: string,
    gameId: string,
    surface: S,
    value: IntegratorGameConfig[S],
  ): Promise<{ status: 'pending_approval'; approval: ApprovalView }> {
    return this.request(
      'PUT',
      `/v1/integrators/${integratorId}/games/${gameId}/config/${surface}`,
      value,
    );
  }

  // ─────────── Approvals
  /** First page (50) of one integrator's approvals. */
  async listApprovals(
    integratorId: string,
    status: ApprovalStatus = 'pending',
  ): Promise<ApprovalView[]> {
    const { approvals } = await this.request<{ approvals: ApprovalView[] }>(
      'GET',
      `/v1/integrators/${integratorId}/approvals?status=${status}`,
    );
    return approvals;
  }

  async approve(approvalId: string): Promise<{ status: 'approved'; approval: ApprovalView }> {
    return this.request('POST', `/v1/approvals/${approvalId}/approve`);
  }

  async reject(
    approvalId: string,
    reason: string,
  ): Promise<{ status: 'rejected'; approval: ApprovalView }> {
    return this.request('POST', `/v1/approvals/${approvalId}/reject`, { reason });
  }

  /**
   * CSV export of the approval audit trail.
   * Returns a downloadable CSV blob over the chosen status + date
   * range. Default: all statuses, all-time, capped server-side at 10k
   * rows. Used by the /approvals page Export button.
   */
  async exportApprovalsCsv(
    integratorId: string,
    opts: { status?: 'pending' | 'approved' | 'rejected' | 'all'; from?: string; to?: string } = {},
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.from) params.set('from', opts.from);
    if (opts.to) params.set('to', opts.to);
    const qs = params.toString();
    const url = `${this.baseUrl}/v1/integrators/${integratorId}/approvals/export${qs ? `?${qs}` : ''}`;
    const res = await this.fetchImpl(url, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) {
      throw new AdminApiError(res.status, 'EXPORT_FAILED', `HTTP ${res.status}`);
    }
    return res.blob();
  }

  /** Provider-wide approvals queue, one page. Closed to tenant staff. */
  async listAllApprovals(
    filters: ApprovalFilters = {},
    page: { limit?: number; cursor?: string } = {},
  ): Promise<ApprovalsPage> {
    const params = approvalFilterParams(filters);
    if (page.limit) params.set('limit', String(page.limit));
    if (page.cursor) params.set('cursor', page.cursor);
    const qs = params.toString();
    return this.request('GET', `/v1/approvals${qs ? `?${qs}` : ''}`);
  }

  /** Count of pending approvals across every integrator — the provider badge. */
  async countPendingApprovals(): Promise<number> {
    const { total } = await this.listAllApprovals({ status: 'pending' }, { limit: 1 });
    return total;
  }

  /** Provider-wide CSV export: the per-integrator columns plus integrator slug,
   *  name and bulk batch id. Default: all statuses, capped server-side at 10k. */
  async exportAllApprovalsCsv(filters: ApprovalFilters = {}): Promise<Blob> {
    const qs = approvalFilterParams({ status: 'all', ...filters }).toString();
    const res = await this.fetchImpl(`${this.baseUrl}/v1/approvals/export${qs ? `?${qs}` : ''}`, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
      credentials: 'include',
    });
    if (!res.ok) {
      throw new AdminApiError(res.status, 'EXPORT_FAILED', `HTTP ${res.status}`);
    }
    return res.blob();
  }

  // ─────────── Bulk config
  /** Per-integrator preview of a bulk change. Writes nothing. */
  async previewBulkConfig(input: BulkProposeInput): Promise<BulkPreview & { dryRun: true }> {
    return this.request('POST', '/v1/bulk/config', { ...input, dryRun: true });
  }

  /** Create one pending batch covering every changed integrator × game. */
  async proposeBulkConfig(
    input: BulkProposeInput,
  ): Promise<BulkPreview & { status: 'pending_approval'; batch: BulkBatchView }> {
    return this.request('POST', '/v1/bulk/config', input);
  }

  async listBulkBatches(
    opts: { status?: BulkBatchStatus | 'all'; limit?: number; cursor?: string } = {},
  ): Promise<{ batches: BulkBatchView[]; nextCursor: string | null }> {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    return this.request('GET', `/v1/bulk${qs ? `?${qs}` : ''}`);
  }

  async getBulkBatch(batchId: string): Promise<BulkBatchDetail> {
    return this.request('GET', `/v1/bulk/${batchId}`);
  }

  /** Apply the batch. Global super_admin other than the proposer. */
  async approveBulkBatch(batchId: string): Promise<BulkDecisionResult> {
    return this.request('POST', `/v1/bulk/${batchId}/approve`);
  }

  async rejectBulkBatch(batchId: string, reason: string): Promise<BulkDecisionResult> {
    return this.request('POST', `/v1/bulk/${batchId}/reject`, { reason });
  }

  /** Replay each integrator's previous value. Integrators changed since the
   *  batch applied are skipped unless `force`. Global super_admin. */
  async revertBulkBatch(
    batchId: string,
    opts: { reason: string; force?: boolean },
  ): Promise<BulkDecisionResult> {
    return this.request('POST', `/v1/bulk/${batchId}/revert`, opts);
  }

  // ─────────── Players
  async listPlayers(
    integratorId: string,
    opts: {
      search?: string;
      cursor?: string;
      limit?: number;
      page?: number;
      status?: 'active' | 'banned';
      identity?: 'named' | 'anon';
    } = {},
  ): Promise<{
    players: PlayerView[];
    nextCursor?: string;
    total?: number;
    page?: number;
    limit?: number;
    stats?: { total: number; banned: number; anonymous: number; totalBalanceCredits: string };
  }> {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.cursor) params.set('cursor', opts.cursor);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.page) params.set('page', String(opts.page));
    if (opts.status) params.set('status', opts.status);
    if (opts.identity) params.set('identity', opts.identity);
    const qs = params.toString();
    return this.request('GET', `/v1/integrators/${integratorId}/players${qs ? `?${qs}` : ''}`);
  }

  async updatePlayerSettings(
    playerId: string,
    patch: {
      isAnonymous?: boolean;
      banned?: boolean;
      soundEnabled?: boolean;
      musicEnabled?: boolean;
      locale?: string;
    },
  ): Promise<{
    id: string;
    isAnonymous: boolean;
    banned: boolean;
    settings: { soundEnabled: boolean; musicEnabled: boolean; locale: string } | null;
  }> {
    return this.request('PATCH', `/v1/players/${playerId}/settings`, patch);
  }

  // ─────────── Game catalog (per-integrator enable/order/badges)
  async listGameCatalog(integratorId: string): Promise<{ games: GameCatalogEntry[] }> {
    return this.request('GET', `/v1/integrators/${integratorId}/game-catalog`);
  }

  async patchGameCatalog(
    integratorId: string,
    gameId: string,
    patch: { enabled?: boolean; displayOrder?: number; badges?: string[] },
  ): Promise<{
    gameId: string;
    enabled: boolean;
    displayOrder: number;
    badges: string[];
    updatedAt: string;
  }> {
    return this.request('PATCH', `/v1/integrators/${integratorId}/game-catalog/${gameId}`, patch);
  }

  /**
   * Provider-scope global game toggle — flips `Game.enabled` and/or the
   * provider-wide per-game maintenance flag for EVERY integrator. Requires a
   * global super_admin grant. Disabled games are rejected at session mint.
   */
  async updateGame(
    gameId: string,
    patch: { enabled?: boolean; maintenance?: { enabled: boolean; message?: string } },
  ): Promise<{
    gameId: string;
    registryEnabled: boolean;
    registryMaintenance: GameMaintenanceFlag;
    updatedAt: string;
  }> {
    return this.request('PATCH', `/v1/games/${gameId}`, patch);
  }

  // ─────────── Webhooks
  async listWebhooks(integratorId: string): Promise<{ webhooks: WebhookView[] }> {
    return this.request('GET', `/v1/integrators/${integratorId}/webhooks`);
  }

  async createWebhook(
    integratorId: string,
    input: { url: string; eventTypes: string[] },
  ): Promise<{
    id: string;
    url: string;
    eventTypes: string[];
    status: string;
    secret: string;
    createdAt: string;
    warning: string;
  }> {
    return this.request('POST', `/v1/integrators/${integratorId}/webhooks`, input);
  }

  async updateWebhook(
    webhookId: string,
    patch: { url?: string; eventTypes?: string[]; status?: 'active' | 'paused' },
  ): Promise<WebhookView> {
    return this.request('PATCH', `/v1/webhooks/${webhookId}`, patch);
  }

  async revokeWebhook(webhookId: string): Promise<{ revokedAt: string }> {
    return this.request('DELETE', `/v1/webhooks/${webhookId}`);
  }

  async deleteWebhook(webhookId: string): Promise<{ deletedAt: string }> {
    return this.request('POST', `/v1/webhooks/${webhookId}/delete`);
  }

  async sendWebhookTest(
    webhookId: string,
  ): Promise<{ id: string; status: string; eventType: string }> {
    return this.request('POST', `/v1/webhooks/${webhookId}/test`);
  }

  async listWebhookDeliveries(
    webhookId: string,
    opts: {
      status?: 'pending' | 'success' | 'failed' | 'dead';
      eventType?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<{ deliveries: WebhookDeliveryView[]; total: number; limit: number; offset: number }> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request('GET', `/v1/webhooks/${webhookId}/deliveries${qs ? `?${qs}` : ''}`);
  }

  async redeliverWebhookDelivery(
    deliveryId: string,
  ): Promise<{ id: string; status: string; scheduledAt: string }> {
    return this.request('POST', `/v1/webhook-deliveries/${deliveryId}/redeliver`);
  }

  // ─────────── Inspector viewer token (used to open a gateway WS connection)
  async inspectorToken(
    integratorId: string,
  ): Promise<{ token: string; integratorId: string; expiresAt: string }> {
    return this.request('POST', `/v1/integrators/${integratorId}/inspector-token`);
  }

  // ─────────── Per-slot totals snapshot (round-engine REST, public)
  async roundTotals(
    roundEngineBaseUrl: string,
    roundId: string,
  ): Promise<{ roundId: string; totals: Record<string, string> }> {
    const res = await this.fetchImpl(`${roundEngineBaseUrl}/v1/rounds/${roundId}/totals`);
    if (!res.ok) {
      throw new AdminApiError(res.status, `HTTP_${res.status}`, res.statusText);
    }
    return (await res.json()) as { roundId: string; totals: Record<string, string> };
  }

  // ─────────── Integrator CRUD
  async createIntegrator(input: {
    slug: string;
    name: string;
    defaultLocale?: string;
    defaultFiatCurrency?: string;
    /** Currencies to enable at creation. Each must exist in the platform
     *  SupportedCurrency registry. The default currency is always enabled even
     *  if omitted here. Omitted ⇒ just the default is enabled. */
    fiatCurrencies?: string[];
    allowedIps?: string[];
    allowedOrigins?: string[];
    walletBaseUrl?: string | null;
    walletTimeoutMs?: number;
    walletDriftTolerance?: number | string;
    /** The integrator's first admin, provisioned in the same transaction and
     *  granted the Integrator Admin capability preset. Omit to create the
     *  integrator with no login of its own. */
    admin?: { email: string; password: string };
  }): Promise<
    IntegratorSummary & {
      /** Currencies enabled at creation (default + extras). */
      fiatCurrencies: string[];
      /** The first admin, when one was requested. The password is never echoed. */
      admin: { id: string; email: string; capabilities: number } | null;
      /** Plaintext wallet auth secret — returned ONCE when minted at create time. */
      walletAuthSecret: string | null;
      walletAuthSecretWarning: string | null;
      /** Starter API key auto-issued so the integrator can mint sessions
       *  immediately. Plaintext shown ONCE. */
      apiKey: {
        id: string;
        plaintextKey: string;
        warning: string;
      };
    }
  > {
    return this.request('POST', '/v1/integrators', input);
  }

  async updateIntegrator(
    integratorId: string,
    patch: {
      name?: string;
      defaultLocale?: string;
      defaultFiatCurrency?: string;
      status?: 'active' | 'suspended';
      // Security + wallet config.
      allowedIps?: string[];
      allowedOrigins?: string[];
      walletBaseUrl?: string | null;
      walletTimeoutMs?: number;
      walletDriftTolerance?: string;
    },
  ): Promise<IntegratorSummary> {
    return this.request('PATCH', `/v1/integrators/${integratorId}`, patch);
  }

  // ─────────── Integrator rates + fiat-currencies
  async listIntegratorRates(
    integratorId: string,
  ): Promise<Array<{ fiatCurrency: string; ratePpm: string; effectiveFrom: string }>> {
    const { rates } = await this.request<{
      rates: Array<{ fiatCurrency: string; ratePpm: string; effectiveFrom: string }>;
    }>('GET', `/v1/integrators/${integratorId}/rates`);
    return rates;
  }

  async listIntegratorRateHistory(
    integratorId: string,
    fiatCurrency?: string,
  ): Promise<
    Array<{
      id: string;
      fiatCurrency: string;
      ratePpm: string;
      effectiveFrom: string;
      setByAdminUserId: string | null;
      note: string | null;
    }>
  > {
    const path = fiatCurrency
      ? `/v1/integrators/${integratorId}/rates/history?fiatCurrency=${encodeURIComponent(fiatCurrency)}`
      : `/v1/integrators/${integratorId}/rates/history`;
    const { history } = await this.request<{
      history: Array<{
        id: string;
        fiatCurrency: string;
        ratePpm: string;
        effectiveFrom: string;
        setByAdminUserId: string | null;
        note: string | null;
      }>;
    }>('GET', path);
    return history;
  }

  async listIntegratorFiatCurrencies(integratorId: string): Promise<
    Array<{ fiatCurrency: string; enabled: boolean }>
  > {
    const { fiatCurrencies } = await this.request<{
      fiatCurrencies: Array<{ fiatCurrency: string; enabled: boolean }>;
    }>('GET', `/v1/integrators/${integratorId}/fiat-currencies`);
    return fiatCurrencies;
  }

  /** Append one or more credit rates (append-only history). ratePpm is a
   *  positive-integer string. Each fiat must already be enabled. */
  async setIntegratorRates(
    integratorId: string,
    rates: Array<{ fiatCurrency: string; ratePpm: string; note?: string }>,
  ): Promise<Array<{ id: string; fiatCurrency: string; ratePpm: string; effectiveFrom: string }>> {
    const { rates: created } = await this.request<{
      rates: Array<{ id: string; fiatCurrency: string; ratePpm: string; effectiveFrom: string }>;
    }>('PATCH', `/v1/integrators/${integratorId}/rates`, { rates });
    return created;
  }

  /** Enable or disable a currency for an integrator. Deposits + balances live
   *  integrator-side (delegated wallet), so apx tracks no deposit bounds. */
  async upsertIntegratorFiatCurrency(
    integratorId: string,
    code: string,
    body: { enabled: boolean },
  ): Promise<{
    fiatCurrency: string;
    enabled: boolean;
    updatedAt: string;
  }> {
    return this.request(
      'PUT',
      `/v1/integrators/${integratorId}/fiat-currencies/${encodeURIComponent(code)}`,
      body,
    );
  }

  // ─────────── Jackpot pools
  async listIntegratorJackpots(integratorId: string): Promise<
    Array<{
      gameId: string;
      poolId: string;
      scope: string;
      balanceCredits: string;
      contributionBps: number;
      minSeedCredits: string;
      lastWonAt: string | null;
      lastWonByPlayerId: string | null;
      updatedAt: string;
      /** Pool currency. Absent on pre-native-currency rows — defaults to IDR. */
      currency?: string;
    }>
  > {
    const { pools } = await this.request<{
      pools: Array<{
        gameId: string;
        poolId: string;
        scope: string;
        balanceCredits: string;
        contributionBps: number;
        minSeedCredits: string;
        lastWonAt: string | null;
        lastWonByPlayerId: string | null;
        updatedAt: string;
        currency?: string;
      }>;
    }>('GET', `/v1/integrators/${integratorId}/jackpots`);
    return pools;
  }

  /** Rotate `Integrator.walletAuthSecret`. Returns the plaintext ONCE. Admin UI
   *  must show a copy-to-clipboard modal and ensure the user has saved it
   *  before the response is discarded. Previous secret is
   *  retained on the integrator row so integrators can accept either during a
   *  24h overlap window. */
  async rotateWalletSecret(
    integratorId: string,
  ): Promise<{ plaintext: string; rotatedAt: string; hadPrevious: boolean; warning: string }> {
    return this.request('POST', `/v1/integrators/${integratorId}/wallet-secret/rotate`);
  }

  /** Drop the previous secret early — closes the overlap window manually
   *  once the integrator confirms they've finished the swap. */
  async discardPrevWalletSecret(integratorId: string): Promise<{ ok: true }> {
    return this.request('POST', `/v1/integrators/${integratorId}/wallet-secret/discard-prev`);
  }

  /** HMAC-sign a sentinel /balance call against the integrator's
   *  walletBaseUrl. Verifies endpoint + secret are wired correctly. Returns
   *  latency + status + (truncated) integrator response body. */
  async testWalletPing(integratorId: string): Promise<WalletTestPingOutcome> {
    return this.request('POST', `/v1/integrators/${integratorId}/wallet/test-ping`);
  }

  /** Re-fire a failed wallet call against the integrator. Idempotent — the
   *  integrator returns the original result if the call was actually applied. */
  async replayWalletCall(
    walletTxId: string,
  ): Promise<{
    id: string;
    replayResult: string;
    newBalanceCredits: string;
    integratorTxId: string | null;
  }> {
    return this.request('POST', `/v1/wallet/calls/${walletTxId}/replay`);
  }

  /** Acknowledge a single failed wallet call — triage-only. Clears it from the
   *  reconciliation "failed calls" list without reconciling balances (drift) or
   *  re-firing the call (replay). The row keeps status='failed'. */
  async acknowledgeWalletCall(
    walletTxId: string,
    note: string,
  ): Promise<{
    id: string;
    acknowledgedAt: string | null;
    acknowledgedBy: string | null;
    acknowledgeNote: string | null;
  }> {
    return this.request('POST', `/v1/wallet/calls/${walletTxId}/acknowledge`, { note });
  }

  /** Bulk-acknowledge up to 500 unacknowledged failed calls for the integrator.
   *  Returns per-row success/failure so the UI can show a summary. */
  async acknowledgeAllWalletCalls(
    integratorId: string,
    args: { note: string; ids?: string[] },
  ): Promise<WalletCallBulkAcknowledgeResult> {
    return this.request(
      'POST',
      `/v1/integrators/${integratorId}/wallet/calls/acknowledge-all`,
      args,
    );
  }

  // ─────────── Auto-play config (per-integrator-game advisory caps)
  async getAutoPlayConfig(
    integratorId: string,
    gameId: string,
  ): Promise<{
    enabled: boolean;
    maxRounds: number;
    maxStake: string | null;
    requireManualFirst: boolean;
  }> {
    return this.request('GET', `/v1/integrators/${integratorId}/games/${gameId}/auto-play`);
  }

  async updateAutoPlayConfig(
    integratorId: string,
    gameId: string,
    patch: {
      enabled?: boolean;
      maxRounds?: number;
      maxStake?: string | null;
      requireManualFirst?: boolean;
    },
  ): Promise<{
    enabled: boolean;
    maxRounds: number;
    maxStake: string | null;
    requireManualFirst: boolean;
  }> {
    return this.request(
      'PATCH',
      `/v1/integrators/${integratorId}/games/${gameId}/auto-play`,
      patch,
    );
  }

  // ─────────── Wallet drift dashboard
  async listWalletDrift(
    integratorId: string,
    opts: {
      status?: 'unresolved' | 'resolved' | 'all';
      playerId?: string;
      search?: string;
      from?: string;
      to?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ): Promise<WalletDriftListResult> {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) {
      if (v !== undefined) q.set(k, String(v));
    }
    const qs = q.toString();
    return this.request('GET', `/v1/integrators/${integratorId}/wallet/drift${qs ? `?${qs}` : ''}`);
  }

  async resolveWalletDrift(
    driftId: string,
    resolutionNote: string,
  ): Promise<{
    id: string;
    resolvedAt: string | null;
    resolvedBy: string | null;
    resolutionNote: string | null;
  }> {
    return this.request('POST', `/v1/wallet/drift/${driftId}/resolve`, { resolutionNote });
  }

  /** Bulk-resolve up to 500 unresolved drift rows for the integrator.
   *  Returns per-row success/failure so the UI can show a summary. */
  async bulkResolveWalletDrift(
    integratorId: string,
    args: { resolutionNote: string; ids?: string[] },
  ): Promise<WalletDriftBulkResolveResult> {
    return this.request('POST', `/v1/integrators/${integratorId}/wallet/drift/resolve-all`, args);
  }

  // ─────────── Player detail (joined wallet + recent bets + tx)
  async fetchPlayerDetail(playerId: string): Promise<PlayerDetail> {
    return this.request('GET', `/v1/players/${playerId}/detail`);
  }

  /** Live integrator balance fetch. Calls wallet service which
   *  signs + posts to the integrator and updates WalletShadow as a side
   *  effect. Used by per-currency "Fetch live" button. */
  async refreshPlayerBalance(playerId: string, fiatCurrency?: string): Promise<LiveBalanceResult> {
    const qs = fiatCurrency ? `?fiatCurrency=${encodeURIComponent(fiatCurrency)}` : '';
    return this.request('POST', `/v1/players/${playerId}/wallet/refresh-balance${qs}`);
  }

  // ─────────── Player balance adjustment (3-mode picker, credit-native)
  async adjustPlayerBalance(
    playerId: string,
    input: {
      mode: 'credit' | 'debit' | 'reconcile_manual';
      /** Credit amount; fiat resolved server-side via session rate. */
      creditAmount: string;
      reason: string;
    },
  ): Promise<{
    mode: 'credit' | 'debit' | 'reconcile_manual';
    playerId: string;
    newBalanceCredits?: string;
    fiatCurrency?: string | null;
    walletTxId: string;
    integratorTxId?: string | null;
  }> {
    return this.request('POST', `/v1/players/${playerId}/balance-adjust`, input);
  }

  // ─────────── Transaction trace + per-player history (audit)
  /** Resolve any pasted id → its owning round's chain trace (integrator-scoped). */
  async getTrace(integratorId: string, id: string): Promise<ChainTrace> {
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/trace?id=${encodeURIComponent(id)}`,
    );
  }

  /** Unified per-player history: round-paginated chain traces, newest first. */
  async getPlayerHistory(
    playerId: string,
    opts: {
      gameId?: string;
      from?: string;
      to?: string;
      cursor?: string;
      limit?: number;
      /** Clock the window is measured against. Server defaults to `settled` —
       *  keep it aligned with the P/L report or the two stop reconciling. */
      basis?: ReportBasis;
    } = {},
  ): Promise<PlayerHistoryResult> {
    const p = new URLSearchParams();
    if (opts.gameId) p.set('gameId', opts.gameId);
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);
    if (opts.cursor) p.set('cursor', opts.cursor);
    if (opts.limit) p.set('limit', String(opts.limit));
    if (opts.basis) p.set('basis', opts.basis);
    return this.request('GET', `/v1/players/${playerId}/history?${p.toString()}`);
  }

  /** True totals (rounds/wagered/won/net) for the same filter set as
   *  getPlayerHistory — computed server-side over the WHOLE range, so the
   *  detail-page tiles don't drift as the operator pages through rounds. */
  async getPlayerHistorySummary(
    playerId: string,
    opts: { gameId?: string; from?: string; to?: string; basis?: ReportBasis } = {},
  ): Promise<PlayerHistorySummary> {
    const p = new URLSearchParams();
    if (opts.gameId) p.set('gameId', opts.gameId);
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);
    if (opts.basis) p.set('basis', opts.basis);
    const qs = p.toString();
    return this.request('GET', `/v1/players/${playerId}/history/summary${qs ? `?${qs}` : ''}`);
  }

  /** Downloadable CSV URL for a single trace (one row per movement). */
  traceCsvUrl(integratorId: string, id: string): string {
    return `${this.baseUrl}/v1/integrators/${integratorId}/trace.csv?id=${encodeURIComponent(id)}`;
  }

  /** Downloadable CSV URL for a player's history (capped, no pagination).
   *  Pass the active game/date filters so the export matches what's on screen. */
  playerHistoryCsvUrl(
    playerId: string,
    opts: { gameId?: string; from?: string; to?: string; basis?: ReportBasis } = {},
  ): string {
    const p = new URLSearchParams();
    if (opts.gameId) p.set('gameId', opts.gameId);
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);
    if (opts.basis) p.set('basis', opts.basis);
    const qs = p.toString();
    return `${this.baseUrl}/v1/players/${playerId}/history.csv${qs ? `?${qs}` : ''}`;
  }

  // ─────────── API keys
  // ─────────── Tenant staff — an integrator managing its own colleagues.
  // The catalogue is server-authoritative: it returns only what the CALLING
  // admin may pass on, so the checklist can never offer a box that would be
  // refused on save.
  async tenantStaffCatalogue(integratorId: string): Promise<TenantStaffCatalogue> {
    return this.request<TenantStaffCatalogue>(
      'GET',
      `/v1/integrators/${integratorId}/staff/catalogue`,
    );
  }

  async listTenantStaff(integratorId: string): Promise<{ staff: TenantStaffMember[] }> {
    return this.request<{ staff: TenantStaffMember[] }>(
      'GET',
      `/v1/integrators/${integratorId}/staff`,
    );
  }

  async createTenantStaff(
    integratorId: string,
    input: { email: string; password: string; permissions: string[] },
  ): Promise<TenantStaffMember> {
    return this.request<TenantStaffMember>(
      'POST',
      `/v1/integrators/${integratorId}/staff`,
      input,
    );
  }

  async setTenantStaffCapabilities(
    integratorId: string,
    userId: string,
    permissions: string[],
  ): Promise<{ id: string; permissions: string[] }> {
    return this.request<{ id: string; permissions: string[] }>(
      'PUT',
      `/v1/integrators/${integratorId}/staff/${userId}/capabilities`,
      { permissions },
    );
  }

  async removeTenantStaff(integratorId: string, userId: string): Promise<void> {
    await this.request<void>('DELETE', `/v1/integrators/${integratorId}/staff/${userId}`);
  }

  async listApiKeys(integratorId: string): Promise<{ keys: ApiKeyView[] }> {
    const raw = (await this.request('GET', `/v1/integrators/${integratorId}/api-keys`)) as {
      apiKeys?: ApiKeyView[];
      keys?: ApiKeyView[];
    };
    // Backend currently returns { apiKeys }; tolerate either shape.
    const keys = raw.apiKeys ?? raw.keys ?? [];
    return { keys };
  }

  async createApiKey(
    integratorId: string,
  ): Promise<{ id: string; plaintextKey: string; createdAt: string }> {
    const raw = await this.request<{
      id: string;
      plaintextKey?: string;
      key?: string;
      createdAt: string;
    }>('POST', `/v1/integrators/${integratorId}/api-keys`, {});
    return {
      id: raw.id,
      plaintextKey: raw.plaintextKey ?? raw.key ?? '',
      createdAt: raw.createdAt,
    };
  }

  async revokeApiKey(keyId: string): Promise<{ revokedAt: string }> {
    return this.request('DELETE', `/v1/api-keys/${keyId}`);
  }

  // ─────────── Audit log
  async listAuditLog(
    opts: {
      integratorId?: string;
      action?: string;
      actorUserId?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ): Promise<{ entries: AuditEntry[]; nextCursor?: string }> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request('GET', `/v1/audit${qs ? `?${qs}` : ''}`);
  }

  // ─────────── Audit hash chains
  async listAuditChainHeads(): Promise<{ heads: AuditChainHead[] }> {
    return this.request('GET', '/v1/audit/chains');
  }

  async verifyAuditChain(
    integratorId: string,
    table: AuditChainTable,
  ): Promise<{
    integratorId: string;
    table: AuditChainTable;
    rowCount: number;
    verdict: AuditChainVerdict;
  }> {
    return this.request('POST', `/v1/audit/verify/${integratorId}/${table}`);
  }

  // ─────────── Audit Merkle anchors
  async listAuditAnchors(
    opts: {
      integratorId?: string;
      tableName?: AuditChainTable;
      from?: string;
      to?: string;
      limit?: number;
    } = {},
  ): Promise<{ anchors: AuditAnchor[] }> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request('GET', `/v1/audit/anchors${qs ? `?${qs}` : ''}`);
  }

  async runAuditAnchors(day?: string): Promise<{
    day: string;
    anchorCount: number;
    anchors: Array<{
      integratorId: string;
      tableName: AuditChainTable;
      merkleRoot: string;
      rowCount: number;
    }>;
  }> {
    return this.request('POST', '/v1/audit/anchors/run', day ? { day } : {});
  }

  // ─────────── Round explorer
  async listRounds(
    integratorId: string,
    opts: {
      gameId?: string;
      state?: string;
      incomplete?: boolean;
      from?: string;
      to?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ): Promise<{ rounds: RoundExplorerView[]; nextCursor?: string }> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request('GET', `/v1/integrators/${integratorId}/rounds${qs ? `?${qs}` : ''}`);
  }

  async listRoundBets(roundId: string): Promise<{ roundId: string; bets: BetView[] }> {
    return this.request('GET', `/v1/rounds/${roundId}/bets`);
  }

  /** Mint a signed 15-min deep-link to the hosted round report (or /verify fallback).
   *  Pass betId to scope the report to one bet (page opens in bet view). */
  async getRoundReportUrl(roundId: string, betId?: string): Promise<{ reportUrl: string }> {
    const qs = betId ? `?betId=${encodeURIComponent(betId)}` : '';
    return this.request('GET', `/v1/admin/rounds/${encodeURIComponent(roundId)}/report-url${qs}`);
  }

  // ─────────── Reports
  /** Player-retention report for a (integrator, game): daily DAU split into new
   *  vs returning, plus D1/D7 cohort return rates. Backs the Reports → Retention tile. */
  async fetchRetention(
    integratorId: string,
    gameId: string,
    opts: { days?: number } = {},
  ): Promise<RetentionReport> {
    const params = new URLSearchParams();
    if (opts.days !== undefined) params.set('days', String(opts.days));
    const qs = params.toString();
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/games/${encodeURIComponent(gameId)}/retention${qs ? `?${qs}` : ''}`,
    );
  }

  /** Players active on one retention day, split new vs returning (drill-down). */
  async fetchRetentionDay(
    integratorId: string,
    gameId: string,
    opts: { date: string; days?: number },
  ): Promise<RetentionDayReport> {
    const params = new URLSearchParams();
    params.set('date', opts.date);
    if (opts.days !== undefined) params.set('days', String(opts.days));
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/games/${encodeURIComponent(gameId)}/retention/day?${params.toString()}`,
    );
  }

  async fetchGgrReport(
    integratorId: string,
    opts: {
      gameId?: string;
      since?: '1h' | '24h' | '7d' | '30d';
      from?: string;
      to?: string;
      player?: string;
      /** Narrows to one native currency (Bet.currency, e.g. 'IDR'/'THB'). */
      currency?: string;
      /** Clock the window is measured against. Server defaults to `settled`. */
      basis?: ReportBasis;
    } = {},
  ): Promise<GgrReport> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request('GET', `/v1/integrators/${integratorId}/reports/ggr${qs ? `?${qs}` : ''}`);
  }

  async fetchPerGameReport(
    integratorId: string,
    opts: {
      since?: '1h' | '24h' | '7d' | '30d';
      from?: string;
      to?: string;
      player?: string;
      /** Narrows to one native currency (Bet.currency). */
      currency?: string;
      /** Clock the window is measured against. Server defaults to `settled`. */
      basis?: ReportBasis;
    } = {},
  ): Promise<PerGameReport> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/reports/per-game${qs ? `?${qs}` : ''}`,
    );
  }

  async fetchTimeseries(
    integratorId: string,
    opts: {
      gameId?: string;
      since?: '24h' | '7d' | '30d';
      from?: string;
      to?: string;
      bucket?: 'hour' | 'day';
      /** IANA tz name — buckets on wall-clock boundaries in this tz instead of UTC. */
      tz?: string;
      /** Narrows to one native currency (Bet.currency). */
      currency?: string;
      /** Clock the window is measured against. Server defaults to `settled`. */
      basis?: ReportBasis;
    } = {},
  ): Promise<TimeseriesReport> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/reports/timeseries${qs ? `?${qs}` : ''}`,
    );
  }

  async fetchTopBettors(
    integratorId: string,
    opts: {
      gameId?: string;
      since?: '1h' | '24h' | '7d' | '30d';
      from?: string;
      to?: string;
      page?: number;
      limit?: number;
      /** Narrows to one native currency (Bet.currency). */
      currency?: string;
      /** Clock the window is measured against. Server defaults to `settled`. */
      basis?: ReportBasis;
    } = {},
  ): Promise<TopBettorsReport> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/reports/top-bettors${qs ? `?${qs}` : ''}`,
    );
  }

  async fetchRecentBigWins(
    integratorId: string,
    opts: {
      gameId?: string;
      since?: '1h' | '24h' | '7d' | '30d';
      from?: string;
      to?: string;
      page?: number;
      limit?: number;
      /** Narrows to one native currency (Bet.currency). */
      currency?: string;
      /** Clock the window is measured against. Server defaults to `settled`. */
      basis?: ReportBasis;
    } = {},
  ): Promise<RecentBigWinsReport> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/reports/recent-big-wins${qs ? `?${qs}` : ''}`,
    );
  }

  /** Flat chronological bet feed (all outcomes) with per-bet money, backing
   *  the Player game log. Rolled-back bets are listed like any other; their
   *  `acceptStatus` says which rollback it was. */
  async fetchRecentBets(
    integratorId: string,
    opts: {
      gameId?: string;
      outcome?: 'WIN' | 'LOSE' | 'VOID';
      since?: '1h' | '24h' | '7d' | '30d';
      from?: string;
      to?: string;
      page?: number;
      limit?: number;
      /** Narrows to one native currency (Bet.currency). */
      currency?: string;
      /** Clock the window is measured against. Server defaults to `settled`. */
      basis?: ReportBasis;
      /** Paste-an-id search: matches the wire transaction id (`Bet.requestId`),
       *  round id, bet id, or a fragment of the transaction id. */
      q?: string;
    } = {},
  ): Promise<RecentBetsReport> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/reports/recent-bets${qs ? `?${qs}` : ''}`,
    );
  }

  /** Flat capped bet ledger backing the P/L Excel export's "Plays" sheet and
   *  the P/L report's on-screen Plays card. `player` searches id/integrator
   *  ref/username (same shape as `listPlayers`); `limit` overrides the
   *  server's PLAYS_CAP default for a smaller on-screen page. */
  async fetchPlays(
    integratorId: string,
    opts: {
      gameId?: string;
      since?: '1h' | '24h' | '7d' | '30d';
      from?: string;
      to?: string;
      player?: string;
      limit?: number;
      /** Narrows to one native currency (Bet.currency). */
      currency?: string;
      /** Clock the window is measured against. Server defaults to `settled`. */
      basis?: ReportBasis;
      /** Paste-an-id search: matches the wire transaction id (`Bet.requestId`),
       *  round id, bet id, or a fragment of the transaction id. */
      q?: string;
    } = {},
  ): Promise<PlaysReport> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request('GET', `/v1/integrators/${integratorId}/reports/plays${qs ? `?${qs}` : ''}`);
  }

  /**
   * Cross-check a list of the integrator's transaction ids against apx.
   *
   * `ids` are the values from their own transaction list — the same strings apx
   * put on the wire as `tx_id`. Returns which ids apx also holds, which it has
   * never seen, and which apx transactions are absent from the submitted list.
   * A matched row carrying `inWindow: false` is the boundary case worth
   * looking for: both systems hold it, dated to different days.
   */
  async reconcileTransactions(
    integratorId: string,
    body: {
      ids: string[];
      since?: '1h' | '24h' | '7d' | '30d';
      from?: string;
      to?: string;
      gameId?: string;
      basis?: ReportBasis;
    },
  ): Promise<ReconcileReport> {
    return this.request('POST', `/v1/integrators/${integratorId}/reports/reconcile`, body);
  }

  async fetchAutoPlaySessions(
    integratorId: string,
    opts: { since?: '1h' | '24h' | '7d' | '30d'; page?: number; limit?: number } = {},
  ): Promise<{
    integratorId: string;
    since: string;
    page: number;
    limit: number;
    total: number;
    totalCapped: boolean;
    sessions: Array<{
      sessionId: string;
      playerId: string;
      displayName: string;
      target: string;
      rounds: number;
      /** The player's currency (fixed at registration). The three amounts are
       *  integer minor units of it. */
      currency: string;
      totalStaked: string;
      totalPayout: string;
      net: string;
      firstAt: string;
      lastAt: string;
    }>;
  }> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) params.set(k, String(v));
    const qs = params.toString();
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/reports/auto-play-sessions${qs ? `?${qs}` : ''}`,
    );
  }

  /** Provider-level report by integrator — one row per (integrator, currency)
   *  pair over the requested window. Provider-global (no integratorId path
   *  param), unlike the per-integrator report fetchers above. */
  async fetchProviderIntegratorReport(
    params: {
      since?: string;
      from?: string;
      to?: string;
      currency?: string;
      gameId?: string;
    } = {},
  ): Promise<ProviderIntegratorReport> {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) query.set(k, String(v));
    const qs = query.toString();
    return this.request('GET', `/v1/reports/by-integrator${qs ? `?${qs}` : ''}`);
  }

  async fetchProviderGameReport(
    params: { since?: string; from?: string; to?: string; currency?: string; integratorId?: string } = {},
  ): Promise<ProviderGameReport> {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) query.set(k, String(v));
    const qs = query.toString();
    return this.request('GET', `/v1/reports/by-game${qs ? `?${qs}` : ''}`);
  }

  async fetchProviderPlayerReport(
    params: {
      since?: string;
      from?: string;
      to?: string;
      currency?: string;
      integratorId?: string;
      gameId?: string;
    } = {},
  ): Promise<ProviderPlayerReport> {
    const query = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) query.set(k, String(v));
    const qs = query.toString();
    return this.request('GET', `/v1/reports/by-player${qs ? `?${qs}` : ''}`);
  }

  // ─────────── Jackpot
  /** Live pool snapshot. Routed through admin-api's same-origin proxy rather
   *  than calling round-engine directly — a direct call relied on an edge
   *  `/round-engine/*` route some deployments lack, so the browser got the SPA's
   *  index.html back and JSON parsing failed with "Unexpected token '<'" (see
   *  `verifyRound`). The legacy first arg (round-engine base URL) is accepted but
   *  ignored for call-site compatibility. */
  async fetchJackpot(
    _roundEngineBaseUrl: string,
    integratorId: string,
    gameId: string,
    currency?: string,
  ): Promise<JackpotSnapshot> {
    const qs = currency ? `?currency=${encodeURIComponent(currency)}` : '';
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/games/${encodeURIComponent(gameId)}/jackpot/live${qs}`,
    );
  }

  async forceReseedJackpot(
    integratorId: string,
    gameId: string,
    currency?: string,
  ): Promise<{
    ok: boolean;
    currency: string;
    pools: Array<{ poolId: string; pool: string; previous: string }>;
  }> {
    const qs = currency ? `?currency=${encodeURIComponent(currency)}` : '';
    return this.request(
      'POST',
      `/v1/integrators/${integratorId}/games/${gameId}/config/jackpot/force-reseed${qs}`,
    );
  }

  // ─────────── Leaderboard
  /** Live preview. Routed through admin-api's same-origin proxy for the same
   *  reason as `fetchJackpot` — a direct round-engine call returns the SPA's
   *  index.html on deployments without an edge `/round-engine/*` route, breaking
   *  JSON parse with "Unexpected token '<'". First arg ignored for compat. */
  async fetchLeaderboard(
    _roundEngineBaseUrl: string,
    integratorId: string,
    gameId: string,
    metric: 'income' | 'turnover' = 'income',
  ): Promise<LeaderboardResponse> {
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/games/${encodeURIComponent(gameId)}/leaderboard?metric=${metric}`,
    );
  }

  async resetLeaderboard(
    integratorId: string,
    gameId: string,
  ): Promise<{ ok: boolean; deletedKeys: number }> {
    return this.request(
      'POST',
      `/v1/integrators/${integratorId}/games/${gameId}/config/leaderboard/reset`,
    );
  }

  // ─────────── Big-winner preview impact
  async previewBigWinner(
    integratorId: string,
    gameId: string,
    opts: { since?: '1h' | '24h' | '7d' | '30d'; threshold?: string } = {},
  ): Promise<BigWinnerPreviewResponse> {
    const params = new URLSearchParams();
    if (opts.since) params.set('since', opts.since);
    if (opts.threshold) params.set('threshold', opts.threshold);
    const qs = params.toString();
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/games/${gameId}/config/big-winner/preview${qs ? `?${qs}` : ''}`,
    );
  }

  // ─────────── RNG dry-run simulator
  async simulateWeights(
    integratorId: string,
    input: { rounds: number; betPerSlot?: string; weights?: WeightsOverride },
  ): Promise<SimulateResponse> {
    return this.request(
      'POST',
      `/v1/integrators/${integratorId}/games/greedyking/config/weights/simulate`,
      input,
    );
  }

  /** Live empirical RTP for a game over a trailing window, from real settled
   *  rounds. Mirrors the Prometheus `apx_rtp` gauge. */
  async getRtp(integratorId: string, gameId: string, hours = 24): Promise<GameRtpResponse> {
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/games/${gameId}/rtp?hours=${hours}`,
    );
  }

  /** Measured RTP aggregated across every integrator — the number the provider
   *  "all integrators" scope wants. The per-integrator `getRtp` reads 0 in that
   *  scope because the provider baseline integrator has no play of its own. */
  async getRtpAllIntegrators(gameId: string, hours = 24): Promise<GameRtpResponse> {
    return this.request('GET', `/v1/games/${encodeURIComponent(gameId)}/rtp?hours=${hours}`);
  }

  /** Configured ("set") RTP — the headline RTP derived from the effective
   *  config (weights for greedyking, multipliers for sicbo/dragontiger). */
  async getRtpSummary(integratorId: string, gameId: string): Promise<RtpSummary> {
    return this.request(
      'GET',
      `/v1/integrators/${integratorId}/games/${encodeURIComponent(gameId)}/config/rtp-summary`,
    );
  }

  /**
   * Target-RTP solver (sicbo / dragontiger only). Given a single broadcast
   * `target` (applied to every bet type independently) and/or per-bet-type
   * `targets`, returns a suggested `multipliers` surface that lands every bet
   * at/under the target, plus the resulting per-bet RTP breakdown and any solver
   * warnings. No persistence — the caller reviews then submits the
   * `suggestedConfig.multipliers` via the normal `proposeSurface` flow.
   *
   * The server 400s `RTP_CAP_EXCEEDED` for a >100% target unless the caller is a
   * super_admin, and `UNSUPPORTED_GAME` for greedyking.
   */
  async solveRtp(
    integratorId: string,
    gameId: string,
    body: { target?: number; targets?: Record<string, number> },
  ): Promise<SolveRtpResponse> {
    return this.request(
      'POST',
      `/v1/integrators/${integratorId}/games/${gameId}/config/solve-rtp`,
      body,
    );
  }

  // ─────────── Jackpot (provider-scope, cross-integrator)
  async fetchJackpotSummary(): Promise<JackpotSummaryResponse> {
    return this.request('GET', `/v1/jackpot`);
  }

  async fetchJackpotDetail(
    gameId: string,
    opts: { since?: '1h' | '24h' | '7d' | '30d'; limit?: number } = {},
  ): Promise<JackpotDetailResponse> {
    const params = new URLSearchParams();
    if (opts.since) params.set('since', opts.since);
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return this.request('GET', `/v1/jackpot/${gameId}${qs ? `?${qs}` : ''}`);
  }

  /** Drain history sourced from JACKPOT_PAYOUT WalletTransaction
   *  rows. Carries winner playerId, share, fiat snapshot, and roundId from
   *  metadata. Cross-integrator unless `integratorId` is set. */
  async fetchJackpotDrains(
    gameId: string,
    opts: {
      since?: '1h' | '24h' | '7d' | '30d' | 'all';
      limit?: number;
      integratorId?: string;
    } = {},
  ): Promise<JackpotDrainResponse> {
    const params = new URLSearchParams();
    if (opts.since) params.set('since', opts.since);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.integratorId) params.set('integratorId', opts.integratorId);
    const qs = params.toString();
    return this.request('GET', `/v1/jackpot/${gameId}/drains${qs ? `?${qs}` : ''}`);
  }

  // ─────────── Provably-fair verifier (public — routes through round-engine)
  /**
   * Provably-fair verify. Routed through admin-api (`/v1/rounds/:id/verify`,
   * same-origin) rather than calling round-engine directly — a direct call
   * relied on an edge `/round-engine/*` route some deployments don't have, so
   * the browser got the SPA's index.html back and JSON parsing failed with
   * "Unexpected token '<'". Going through admin-api uses the same `/v1/*` path
   * the rest of the SDK already uses, so it works on every environment.
   *
   * The legacy first arg (round-engine base URL) is accepted but ignored for
   * call-site compatibility.
   */
  async verifyRound(roundIdOrBase: string, maybeRoundId?: string): Promise<VerifyResponse> {
    const roundId = maybeRoundId ?? roundIdOrBase;
    return this.request<VerifyResponse>('GET', `/v1/rounds/${roundId}/verify`);
  }
}

export interface SimulateResponse {
  rounds: number;
  /** Empirical overall RTP from the production-path simulator (incl. specials). */
  empiricalRtp: number;
  /** Deterministic primary-tier RTP (specials are pool-driven, excluded). */
  theoreticalRtp: number;
  /** Highest single-food RTP — the binding "exploitable slot" number. */
  worstFoodRtp: number;
  specialFreq: number;
  pizzaFreq: number;
  saladFreq: number;
  perSideRtp: { salad: number; pizza: number };
  jackpotCount: number;
  /** Empirical hit counts for the primary tier (8 foods + house). */
  primaryCounts: Record<string, number>;
  /** Empirical hit counts for the special tier (pizza/salad/none). */
  specialCounts: Record<string, number>;
  perItemRtp: Record<string, number>;
  theoreticalPerItemRtp: Record<string, number>;
  theoreticalPerItemHitRate: Record<string, number>;
}

/** Configured ("set") RTP derived from the effective game config. */
export interface RtpSummary {
  gameId: string;
  source: 'target' | 'multipliers' | 'unknown';
  /** Headline RTP (0..1): configured target for greedyking, max bet RTP for solver games. */
  headlineRtp: number | null;
  headlineLabel: string;
  perBetType: Array<{ betType: string; rtp: number }>;
}

/** Live empirical RTP over a trailing window (admin RTP monitor). */
export interface GameRtpResponse {
  /** null for the provider "all integrators" aggregate. */
  integratorId: string | null;
  gameId: string;
  windowHours: number;
  rounds: number;
  staked: string;
  paid: string;
  /** Σ payout / Σ bet, or null when no bets landed in the window. */
  rtp: number | null;
  perBetType: Array<{
    betType: string;
    bets: number;
    staked: string;
    paid: string;
    rtp: number | null;
  }>;
}

/**
 * Result of the Target-RTP solver. `suggestedConfig` is the would-be-effective
 * full game config (bigint fields normalized to strings by the server's
 * `jsonSafe`), carrying a fresh, self-consistent `multipliers` surface under
 * `suggestedConfig.multipliers` — that nested object is the bps-integer payload
 * to propose via `proposeSurface(..., 'multipliers', ...)`. `rtpBreakdown` maps
 * each bet type to its resulting RTP (1.0 = 100%).
 */
export interface SolveRtpResponse {
  suggestedConfig: { multipliers?: unknown } & Record<string, unknown>;
  rtpBreakdown: Record<string, number>;
  warnings: string[];
}

export interface ApiKeyView {
  id: string;
  integratorId: string;
  status: string;
  scopes: string[];
  rateLimit: number;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface AuditEntry {
  id: string;
  actorUserId: string;
  actorEmail: string;
  integratorId: string | null;
  action: string;
  targetType: string;
  targetId: string;
  before: unknown;
  after: unknown;
  createdAt: string;
}

// ─────────── Audit hash chains

export type AuditChainTable = 'round_audit' | 'wallet_transaction' | 'admin_audit';

export interface AuditChainHead {
  integratorId: string;
  tableName: AuditChainTable | string;
  lastRowId: string;
  lastHash: string;
  rowCount: number;
  updatedAt: string;
}

export type AuditChainVerdict =
  | { ok: true; count: number; headHash: string | null }
  | {
      ok: false;
      brokenAtRowId: string;
      reason: 'prev_hash_mismatch' | 'row_hash_mismatch' | 'signature_mismatch' | 'missing_fields';
      expected?: { prevHash?: string | null; rowHash?: string };
      actual?: { prevHash?: string | null; rowHash?: string | null; signature?: string | null };
      count: number;
    };

export interface AuditAnchor {
  integratorId: string;
  tableName: AuditChainTable | string;
  /** YYYY-MM-DD UTC. */
  day: string;
  merkleRoot: string;
  signature: string;
  rowCount: number;
  firstRowId: string | null;
  lastRowId: string | null;
  createdAt: string;
}

export interface RoundExplorerView {
  id: string;
  gameId: string;
  state: string;
  startedAt: string;
  bettingClosesAt: string;
  endedAt: string | null;
  totalBet: string;
  totalPayout: string;
  participantCount: number;
  result: unknown;
  serverSeedHash: string | null;
}

export interface BetView {
  id: string;
  playerId: string;
  betType: string;
  /** The bet's currency (`Bet.currency`). The two amounts are integer minor
   *  units of it (USD in cents). Optional: an older API build omits it. */
  currency?: string;
  creditAmount: string;
  creditPayout: string;
  outcome: string;
  /** @see ChainBet.acceptStatus. Optional: an older API build omits it. */
  acceptStatus?: string;
  placedAt: string;
  settledAt: string | null;
  /** Present on player-detail endpoint; optional on round-bets endpoint
   * (rounded shape is keyed by round so doesn't repeat it). */
  roundId?: string;
  /** Present on player-detail endpoint; absent elsewhere. */
  gameId?: string;
}

export interface WalletView {
  /** Single credit-balance row per (integrator, player). */
  creditBalance: string;
  updatedAt: string;
  /** Marked false because the integrator wallet is the canonical source. */
  canonical?: boolean;
}

export interface WalletTransactionView {
  id: string;
  type: string;
  creditAmount: string;
  fiatAmount: string | null;
  fiatCurrency: string | null;
  ratePpm: string | null;
  balanceAfter: string | null;
  reason: string;
  createdAt: string;
}

export interface WalletDriftEntry {
  id: string;
  playerId: string;
  integratorPlayerRef: string;
  playerUsername: string | null;
  /** The player's currency — the unit of the three balances, which are
   *  integer minor units of it (USD in cents). */
  currency: string;
  expectedCredits: string;
  integratorReportedCredits: string;
  /** Fiat-conversion artifact; null in the native-currency model (money moves
   *  1:1, so no rate is stamped at detection). Retained for pre-native rows. */
  ratePpmAtDetection: string | null;
  delta: string;
  surfacingRequestId: string;
  walletTxId: string | null;
  walletTxSummary: { id: string; type: string; reason: string; requestId: string } | null;
  detectedAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolutionNote: string | null;
}

export interface WalletDriftListResult {
  unresolvedCount: number;
  /** Unresolved across all time — lets the UI flag a backlog outside the window. */
  unresolvedCountAllTime?: number;
  entries: WalletDriftEntry[];
  nextCursor?: string;
}

export interface WalletDriftBulkResolveResult {
  /** Count of rows the server attempted to resolve (after the BULK_MAX cap). */
  totalAttempted: number;
  succeeded: Array<{ id: string }>;
  failed: Array<{ id: string; code: string; message: string }>;
  /** Unresolved count remaining after this batch. */
  remainingUnresolved: number;
  /** Server-side per-call cap, surfaced so the UI can hint when more remain. */
  cappedAt: number;
  elapsedMs: number;
}

export interface WalletCallBulkAcknowledgeResult {
  /** Count of rows the server attempted to acknowledge (after the cap). */
  totalAttempted: number;
  succeeded: Array<{ id: string }>;
  failed: Array<{ id: string; code: string; message: string }>;
  /** Failed+unacknowledged count remaining after this batch. */
  remainingUnacknowledged: number;
  /** Server-side per-call cap, surfaced so the UI can hint when more remain. */
  cappedAt: number;
  elapsedMs: number;
}

export interface PlayerDetail {
  player: PlayerView;
  wallets: WalletView[];
  recentBets: BetView[];
  recentTransactions: WalletTransactionView[];
  /** Count of unresolved WalletDrift rows for this player. */
  driftCount?: number;
  /** Enabled fiat currencies for the player's integrator, for
   *  per-currency live-fetch UI. */
  enabledFiats?: string[];
  /** Every game the player has bet on — full option set for the history game
   *  filter (not limited to the recent-bets window). */
  games?: string[];
}

export interface LiveBalanceResult {
  creditBalance: string;
  fiatCurrency: string;
  fetchedAt: string;
}

export interface GameMaintenanceFlag {
  enabled: boolean;
  message?: string;
}

export interface GameCatalogEntry {
  gameId: string;
  name: unknown;
  type: string;
  scope: string;
  registryEnabled: boolean;
  /** Provider-wide per-game maintenance flag (applies to every integrator). */
  registryMaintenance: GameMaintenanceFlag;
  integrator: {
    enabled: boolean;
    displayOrder: number;
    badges: string[];
    updatedAt: string;
  } | null;
}

export interface WebhookView {
  id: string;
  url: string;
  eventTypes: string[];
  status: string;
  retryPolicy?: unknown;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDeliveryView {
  id: string;
  eventType: string;
  status: string;
  attempt: number;
  lastError: string | null;
  /** The delivered event body (shown in the expandable delivery row). */
  payload?: unknown;
  scheduledAt: string;
  deliveredAt: string | null;
}

/**
 * Per-currency fiat companion for a P/L aggregate. The credit figures are the
 * game-native unit; `FiatAgg` carries the integrator-side fiat (summed from the
 * stored `WalletTransaction.fiatAmount`, exact — rates drift, so it is not a
 * scalar multiply of credits). Usually one entry per report; a tenant can
 * transact in multiple currencies, hence an array.
 */
export interface FiatAgg {
  currency: string;
  totalBet: string;
  totalPayout: string;
  houseTake: string;
}

/**
 * Per-currency credit-native companion, keyed on `Bet.currency` (stamped at
 * placement). Unlike `FiatAgg` (integrator-side fiat), this sums the `Bet`
 * ledger's own credit columns directly — no rate, no join. Prod is currently
 * single-currency (IDR), so this yields one entry today; the shape exists
 * for multi-currency integrators (e.g. the sample integrator's THB/USD test
 * currencies).
 */
export interface CurrencyAgg {
  currency: string;
  totalBet: string;
  totalPayout: string;
  houseTake: string;
  betCount: number;
  rtp: number;
}

export interface RetentionReport {
  windowDays: number;
  daily: Array<{ date: string; active: number; new: number; returning: number }>;
  cohort: {
    d1Rate: number | null;
    d7Rate: number | null;
    d1Cohort: number;
    d7Cohort: number;
  };
}

export interface RetentionDayReport {
  date: string;
  players: Array<{
    playerId: string;
    displayName: string;
    kind: 'new' | 'returning';
    betCount: number;
  }>;
}

/**
 * Which clock a report window is measured against.
 *
 * `settled` (the server default) dates a bet on `Bet.settledAt`, matching how
 * an integrator's transaction list dates the same transaction — so a bet placed
 * at 23:59:59 and settled at 00:00:03 lands on the same day in both systems.
 * `placed` dates on `Bet.placedAt`, apx's historical basis, kept so a
 * previously-exported figure stays reproducible.
 */
export type ReportBasis = 'placed' | 'settled';

export interface PerGameReport {
  integratorId: string;
  since: '1h' | '24h' | '7d' | '30d';
  basis: ReportBasis;
  games: Array<{
    gameId: string;
    totalBet: string;
    totalPayout: string;
    houseTake: string;
    rtp: number;
    betCount: number;
    /** Per-currency fiat companion (empty if no wallet rows in window). */
    fiat: FiatAgg[];
    /** Per-currency credit-native breakdown (Bet.currency). */
    byCurrency: CurrencyAgg[];
  }>;
}

export interface TimeseriesReport {
  integratorId: string;
  gameId: string;
  since: '24h' | '7d' | '30d';
  basis: ReportBasis;
  bucket: 'hour' | 'day';
  points: Array<{
    t: string;
    totalBet: string;
    totalPayout: string;
    houseTake: string;
    betCount: number;
    /** Per-currency fiat companion for this bucket (empty if none). */
    fiat: FiatAgg[];
    /** Per-currency credit-native breakdown for this bucket (Bet.currency). */
    byCurrency: CurrencyAgg[];
  }>;
}

export interface TopBettorsReport {
  integratorId: string;
  since: '1h' | '24h' | '7d' | '30d';
  page: number;
  limit: number;
  total: number;
  basis: ReportBasis;
  /** True when `total` is clamped to the server cap (real count is higher). */
  totalCapped: boolean;
  entries: Array<{
    rank: number;
    playerId: string;
    displayName: string;
    totalBet: string;
    totalPayout: string;
    net: string;
    betCount: number;
    /** Per-currency fiat companion for this player (empty if none). */
    fiat: FiatAgg[];
    /** The player's fixed currency (one player, one currency, set at
     *  registration) — sourced directly from `Bet.currency`. */
    currency: string;
  }>;
}

export interface RecentBigWinsReport {
  integratorId: string;
  since: '1h' | '24h' | '7d' | '30d';
  page: number;
  limit: number;
  total: number;
  basis: ReportBasis;
  wins: Array<{
    betId: string;
    /** @see TRANSACTION_ID_NOTE */
    transactionId: string;
    roundId: string;
    playerId: string;
    displayName: string;
    gameId: string;
    betType: string;
    /** The bet's currency (`Bet.currency`, stamped at placement). The two
     *  amounts are integer minor units of it. */
    currency: string;
    creditAmount: string;
    creditPayout: string;
    placedAt: string;
    settledAt: string | null;
  }>;
}

export interface RecentBetsReport {
  integratorId: string;
  since: '1h' | '24h' | '7d' | '30d';
  page: number;
  limit: number;
  total: number;
  basis: ReportBasis;
  bets: Array<{
    betId: string;
    /** @see TRANSACTION_ID_NOTE */
    transactionId: string;
    roundId: string;
    playerId: string;
    displayName: string;
    gameId: string;
    betType: string;
    outcome: 'WIN' | 'LOSE' | 'VOID';
    /** @see ChainBet.acceptStatus */
    acceptStatus: string;
    /** The bet's currency (`Bet.currency`, stamped at placement). */
    currency: string;
    creditAmount: string;
    creditPayout: string;
    net: string;
    placedAt: string;
    settledAt: string | null;
  }>;
}

/** Flat bet ledger for the P/L Excel export's "Plays" sheet — one capped
 *  batch (server-bounded at 10k), no pagination. Includes VOID/cancelled
 *  bets (raw ledger, not the turnover-only P/L view). */
export interface PlaysReport {
  basis: ReportBasis;
  plays: Array<{
    betId: string;
    /** @see TRANSACTION_ID_NOTE */
    transactionId: string;
    roundId: string;
    playerId: string;
    username: string | null;
    integratorPlayerRef: string;
    gameId: string;
    betType: string;
    outcome: string;
    acceptStatus: string;
    /** The bet's currency (`Bet.currency`, stamped at placement). */
    currency: string;
    creditAmount: string;
    creditPayout: string;
    placedAt: string;
    settledAt: string | null;
  }>;
  /** True when the ledger had more rows than the server cap — the export is
   *  truncated to the newest `PLAYS_CAP` (10k) plays. */
  capped: boolean;
  count: number;
}

/**
 * TRANSACTION_ID_NOTE — `transactionId` is `Bet.requestId`: the game client's
 * idempotency key, which apx also sends to the integrator wallet as the call's
 * `tx_id`. It is therefore the value printed in the integrator's own
 * transaction list, and the only identifier that matches by eye across both
 * systems. Format varies by game (greedyking emits `<epochMs>-<rand>`, sicbo a
 * UUID) — it is an opaque key, never parse it.
 */

/**
 * `/reports/reconcile` response — the three-way split between a submitted list
 * of integrator transaction ids and what apx holds for the same window.
 */
export interface ReconcileReport {
  integratorId: string;
  basis: ReportBasis;
  /** Count of unique ids actually compared (input is de-duplicated). */
  submitted: number;
  /** Ids apx also holds. Resolved WITHOUT a date filter, so `inWindow: false`
   *  marks a transaction both systems have but date to different days. */
  matched: Array<{
    transactionId: string;
    betId: string;
    roundId: string;
    gameId: string;
    playerId: string;
    username: string | null;
    integratorPlayerRef: string;
    outcome: string;
    /** @see ChainBet.acceptStatus */
    acceptStatus: string;
    currency: string;
    creditAmount: string;
    creditPayout: string;
    net: string;
    placedAt: string;
    settledAt: string | null;
    inWindow: boolean;
  }>;
  /** Submitted ids apx has no bet for, under this integrator. */
  missingInApx: string[];
  /** apx transactions inside the window that the submitted list omitted. */
  missingInIntegrator: Array<{
    transactionId: string;
    betId: string;
    roundId: string;
    gameId: string;
    outcome: string;
    /** @see ChainBet.acceptStatus */
    acceptStatus: string;
    currency: string;
    creditAmount: string;
    creditPayout: string;
    net: string;
    placedAt: string;
    settledAt: string | null;
  }>;
  windowCount: number;
  /** True when the window held more rows than the server scanned — the
   *  `missingInIntegrator` half is then incomplete and says so. */
  windowCapped: boolean;
}

/** One row of the provider-level "report by integrator" — per
 *  (integrator, currency) pair, since `Bet.currency` is per-row and metrics
 *  are never blended across currencies. */
export interface ProviderIntegratorReportRow {
  integratorId: string;
  slug: string;
  name: string;
  currency: string;
  players: number;
  betCount: number;
  turnover: string;
  payout: string;
  ggr: string;
  rtp: number;
  jpContribute: string;
}

/** `/v1/reports/by-integrator` response — provider-global, one row per
 *  (integrator, currency) over the requested window. */
export interface ProviderIntegratorReport {
  window: { from: string; to: string };
  rows: ProviderIntegratorReportRow[];
}

/** One row of the provider-level "report by game" — per (gameId, currency)
 *  pair, since `Bet.currency` is per-row and metrics are never blended
 *  across currencies. */
export interface ProviderGameReportRow {
  gameId: string;
  currency: string;
  players: number;
  betCount: number;
  turnover: string;
  payout: string;
  ggr: string;
  rtp: number;
  jpContribute: string;
}

/** `/v1/reports/by-game` response — provider-global, one row per
 *  (gameId, currency) over the requested window. */
export interface ProviderGameReport {
  window: { from: string; to: string };
  rows: ProviderGameReportRow[];
}

/** One row of the provider-level "report by player" — per (playerId,
 *  currency) pair, capped to the top rows by turnover desc. `ggr` is the
 *  house's take (turnover - payout); `net` is the player's own net
 *  (payout - turnover, i.e. `-ggr`). */
export interface ProviderPlayerReportRow {
  playerId: string;
  displayName: string;
  integratorPlayerRef: string;
  integratorId: string;
  currency: string;
  betCount: number;
  turnover: string;
  payout: string;
  ggr: string;
  net: string;
  rtp: number;
}

/** `/v1/reports/by-player` response — provider-global, top rows by turnover
 *  desc over the requested window (capped server-side). */
export interface ProviderPlayerReport {
  window: { from: string; to: string };
  rows: ProviderPlayerReportRow[];
}

export interface GgrReport {
  integratorId: string;
  gameId: string;
  since: '1h' | '24h' | '7d' | '30d';
  basis: ReportBasis;
  totalBet: string;
  totalPayout: string;
  houseTake: string;
  rtp: number;
  betCount: number;
  distinctPlayers: number;
  /** Per-currency fiat companion (empty if no wallet rows in window). */
  fiat: FiatAgg[];
  /** Per-currency credit-native breakdown (Bet.currency). */
  byCurrency: CurrencyAgg[];
}

/**
 * `/reports/jackpot-contribution` response — shape depends on the `groupBy`
 * query param (`'none' | 'game' | 'day'`, default `'game'`).
 */
export interface JackpotContributionTotalReport {
  integratorId: string;
  gameId: string;
  since: '1h' | '24h' | '7d' | '30d';
  totalJpContribute: string;
  totalBet: string;
  totalPayout: string;
  effectiveRate: number;
  betCount: number;
  /** Per-currency credit-native breakdown (Bet.currency). */
  byCurrency: CurrencyAgg[];
}

export interface JackpotContributionByGameReport {
  integratorId: string;
  since: '1h' | '24h' | '7d' | '30d';
  games: Array<{
    gameId: string;
    jpContribute: string;
    totalBet: string;
    totalPayout: string;
    betCount: number;
    /** Per-currency credit-native breakdown (Bet.currency). */
    byCurrency: CurrencyAgg[];
  }>;
}

export interface JackpotContributionByDayReport {
  integratorId: string;
  since: '1h' | '24h' | '7d' | '30d';
  days: Array<{
    day: string;
    jpContribute: string;
    totalBet: string;
    totalPayout: string;
    betCount: number;
    /** Per-currency credit-native breakdown (Bet.currency). */
    byCurrency: CurrencyAgg[];
  }>;
}

/** One veg/meat pool as surfaced by round-engine's live `/v1/jackpot/:gameId`. */
export interface JackpotLivePoolView {
  pool: string;
  seed: string;
  threshold: string;
  fillPct: number;
  baseMult: number;
  maxMult: number;
  currentMult: number;
  maxTriggerRateBps: number;
  rakeBps: number;
}

/**
 * Live jackpot snapshot from round-engine's public `/v1/jackpot/:gameId`.
 * `fetchJackpot()` is only ever called for greedyking today (sicbo's admin
 * page has no live-snapshot card), so the `category` branch is the one that
 * matters in practice — `none` is kept for type-safety / future games.
 * Replaces the deprecated two-tier (`tier1`/`tier2`) shape.
 */
export type JackpotSnapshot =
  | {
      integratorId: string;
      gameId: string;
      model: 'category';
      veg: JackpotLivePoolView;
      meat: JackpotLivePoolView;
      saladMultiplier: number;
      pizzaMultiplier: number;
      /** Pool currency. Absent on pre-native-currency responses — defaults to IDR. */
      currency?: string;
    }
  | { integratorId: string; gameId: string; model: 'none'; pool: string; currency?: string };

export interface LeaderboardResponse {
  integratorId: string;
  gameId: string;
  metric: 'income' | 'turnover';
  windowHours: number;
  topN: number;
  generatedAt: string;
  entries: Array<{
    rank: number;
    playerId: string;
    displayName: string;
    value: string;
  }>;
}

export interface BigWinnerPreviewResponse {
  integratorId: string;
  gameId: string;
  since: '1h' | '24h' | '7d' | '30d';
  threshold: string;
  wouldHaveFired: number;
  topMatches: Array<{
    betId: string;
    roundId: string;
    playerId: string;
    payout: string;
    settledAt: string | null;
  }>;
}

export interface VerifyResponse {
  roundId: string;
  gameId: string;
  serverSeed: string;
  serverSeedHash: string;
  clientSeed: string;
  nonce: number;
  weightTable: {
    version: number;
    primary: Record<string, number>;
    special: Record<string, number>;
  };
  /** Two-tier result `{ primary, special }` stored on the round. */
  expectedResult: { primary: string; special: string | null } | unknown;
  hashMatch: boolean;
  resultMatch: boolean;
  /** Independently re-derived two-tier result. */
  derivedResult: { primary: string; special: string | null };
  derivedPrimaryRoll: number;
  derivedSpecialRoll: number;
  /** Tri-state verdict from round-engine. Newer engines send this; `valid` is
   *  the legacy boolean. Prefer `verdict` and fall back to `valid`. */
  verdict?: 'VALID' | 'INVALID' | 'UNVERIFIABLE';
  valid: boolean;
}

/** One category pool (veg → SALAD, meat → PIZZA) as surfaced by admin-api. */
export interface JackpotCategoryPoolView {
  poolId: 'veg' | 'meat';
  balance: string;
  seed: string;
  threshold: string;
  fillPct: number;
  maxTriggerRateBps: number;
}

/**
 * Provider-scope per-game jackpot summary. Discriminated on `model` —
 * mirrors the three REAL jackpot shapes (see admin-api's `jackpot.ts`):
 *   - `category`   — greedyking's two independent veg/meat linear pools.
 *   - `triple-drain` — sicbo's single pool, deterministic drain on a triple.
 *   - `none`       — no jackpot model at all (dragontiger).
 * Replaces the old deprecated two-tier (`tier1`/`tier2`) shape.
 */
export type JackpotGameSummary =
  | {
      gameId: string;
      model: 'category';
      contributionRateBps: number;
      pools: [JackpotCategoryPoolView, JackpotCategoryPoolView];
      /** Pool currency. Absent on pre-native-currency responses — defaults to IDR. */
      currency?: string;
    }
  | {
      gameId: string;
      model: 'triple-drain';
      /** True when `pool.balance` is a cross-integrator SUM of per-integrator
       *  pools (sicbo) rather than one shared pot. Arm/fill state is
       *  per-integrator and does not apply to the aggregate. Absent on older
       *  backends (treat as a single global pot). */
      aggregate?: boolean;
      /** Number of per-integrator pool rows summed into `pool.balance`. */
      integratorCount?: number;
      /** Human-readable scope note for the provider UI. */
      note?: string;
      pool: { balance: string; minDrainBalanceCredits: string; contributionRate: number };
      /** Pool currency. Absent on pre-native-currency responses — defaults to IDR. */
      currency?: string;
    }
  | { gameId: string; model: 'none'; currency?: string };

export interface JackpotSummaryResponse {
  games: JackpotGameSummary[];
}

export interface JackpotContributionRow {
  roundId: string;
  integratorId: string;
  endedAt: string | null;
  totalBet: string;
  totalPayout: string;
  houseTake: string;
  contribution: string;
  participantCount: number;
}

export interface JackpotHitRow {
  roundId: string;
  integratorId: string;
  endedAt: string | null;
  special: string | null;
  totalPayout: string;
}

/** Single-game detail — the model-specific summary fields (see
 *  `JackpotGameSummary`) plus the rolling-window contribution/hit history. */
export type JackpotDetailResponse = JackpotGameSummary & {
  window: '1h' | '24h' | '7d' | '30d';
  windowSummary: {
    rounds: number;
    /** Subset of `rounds` that actually contributed (house take > 0). Absent
     *  on older backends. */
    contributingRounds?: number;
    totalHouseTake: string;
    totalContribution: string;
  };
  recentContributions: JackpotContributionRow[];
  recentHits: JackpotHitRow[];
};

export interface JackpotDrainRow {
  id: string;
  integratorId: string;
  playerId: string;
  roundId: string | null;
  poolId: string | null;
  creditAmount: string;
  /** Native currency `creditAmount` is denominated in. Absent on pre-native-currency
   *  rows — defaults to IDR. Distinct from `fiatCurrency`, which is the converted display value. */
  currency?: string;
  fiatAmount: string | null;
  fiatCurrency: string | null;
  ratePpm: string | null;
  status: string;
  createdAt: string;
}

export interface JackpotDrainResponse {
  gameId: string;
  drains: JackpotDrainRow[];
}

/** Query string for the provider-wide approvals list/export. Integrator ids are
 *  sent comma-separated, which the server accepts alongside repeated keys. */
function approvalFilterParams(f: ApprovalFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (f.status) params.set('status', f.status);
  if (f.integratorIds?.length) params.set('integratorId', f.integratorIds.join(','));
  if (f.gameId) params.set('gameId', f.gameId);
  if (f.surface) params.set('surface', f.surface);
  if (f.batchId) params.set('batchId', f.batchId);
  if (f.from) params.set('from', f.from);
  if (f.to) params.set('to', f.to);
  return params;
}
